/**
 * Spanish (Spain) UI catalogue — MiLicita, the `es` deployment.
 *
 * Same flat key set and key order as `ru.ts` (the reference); `parity.test.ts`
 * enforces the key set. Every `{{placeholder}}` a caller fills is kept.
 *
 * Country facts are written for Spain, not translated from the Uzbek copy.
 * What Pro sells is the Uzbek Pro set (owner, 2026-10-20: the source record and
 * the documents are not free on Spain either): the órgano de contratación's
 * contact, the pliegos, the link to the original notice, the adjudicatario and
 * importe, market and buyer analytics, competitor tracking, Excel and unlimited
 * alerts. No line sells an AI read of the pliegos, questions over the documents
 * or a fit check — none of them exists on the site. Cards are paid through
 * Stripe; support is {{brandEmail}}.
 *
 * Plural nouns ({{word}}, {{lots}}) are filled in code by the locale-aware
 * helpers (`lib/format.ts` and friends, which return Spanish forms for `es`).
 * Where a caller still builds the word from Russian literals (`pluralRu(…)`),
 * the Spanish string spells the noun out instead, as `uz.ts` does.
 */
export const es = {
  // Nav/chrome
  'nav.catalog': 'Catálogo',
  'nav.tender': 'Licitación',
  'nav.login': 'Entrar',
  'nav.dashboard': 'Panel',
  'nav.signOut': 'Salir',
  // Landing
  'landing.hero.title': 'Encuentra oportunidades de negocio',
  'landing.hero.description':
    'Consulta las licitaciones públicas de toda España. Filtra por comunidad autónoma, categoría y estado.',
  'landing.hero.browseCatalog': 'Abrir el catálogo',
  'landing.recentTenders': 'Últimas licitaciones',
  // Catalog
  'catalog.title': 'Catálogo de licitaciones',
  'catalog.loading': 'Cargando licitaciones…',
  // The one-line notice while PLACSP feed 643 publishes nothing new (watchdog, 21.10).
  'catalog.sourceStale':
    'La Plataforma de Contratación del Sector Público no publica licitaciones nuevas desde el {{date}}. Mostramos lo último disponible.',
  'error.title': 'No se ha podido cargar. Comprueba tu conexión.',
  'error.retry': 'Reintentar',
  'error.searchBusy':
    'Ahora mismo hay muchas búsquedas a la vez. El sitio funciona: vuelve a intentarlo en unos segundos.',
  'error.searchTooMany':
    'Tus búsquedas anteriores todavía se están ejecutando. Espera el resultado y vuelve a intentarlo.',
  'catalog.slowLoad':
    'La búsqueda tarda más de lo normal. El sitio funciona: los resultados aparecerán en unos segundos.',
  'catalog.filters': 'Filtros',
  'catalog.filters.a11yDesc': 'Filtros del catálogo de licitaciones.',
  'catalog.filtersApply': 'Ver resultados',
  'catalog.export': 'Exportar a Excel',
  'catalog.exportProOnly': 'La exportación está disponible en Pro',
  'catalog.exportFailed': 'No se ha podido exportar. Inténtalo de nuevo.',
  'catalog.found': '{{count}} licitaciones encontradas',
  'catalog.ikpuLabel': 'Código CPV',
  'catalog.ikpuAll': 'Todas las categorías',
  'catalog.sortLabel': 'Ordenar',
  'catalog.sort.newest': 'Más recientes',
  'catalog.sort.deadline': 'Cierre más próximo',
  'catalog.sort.budgetDesc': 'Presupuesto: de mayor a menor',
  'catalog.sort.budgetAsc': 'Presupuesto: de menor a mayor',
  'catalog.budgetFrom': 'Presupuesto desde (€)',
  'catalog.budgetTo': 'Presupuesto hasta (€)',
  'catalog.deadlineFrom': 'Plazo desde',
  'catalog.excludeWords': 'Ocultar licitaciones con las palabras',
  'catalog.excludeWordsPlaceholder': 'palabras separadas por comas',
  'catalog.deadlineTo': 'Plazo hasta',
  'catalog.clearFilters': 'Borrar filtros',
  'catalog.moreFilters': 'Más filtros',
  // Tender detail
  'tender.backToIfi': '← Volver a los proyectos de organismos internacionales',
  'tender.backToCatalog': '← Volver al catálogo',
  'tender.loading': 'Cargando la licitación…',
  'tender.sourceLangNote': 'en el idioma de la fuente',
  'tender.scanNotice':
    'Texto reconocido a partir de un documento escaneado. Comprueba importes, plazos, direcciones y datos con el {{original}}.',
  'tender.scanNoticeOriginal': 'original',
  'tender.notFound': 'Licitación no encontrada',
  'tender.notFoundDesc': 'Puede que se haya eliminado o que el enlace esté desactualizado.',
  'tender.loadFailed': 'No se ha podido cargar la licitación',
  'tender.loadFailedDesc': 'Comprueba tu conexión e inténtalo de nuevo.',
  'tender.seoDesc':
    'Licitación «{{title}}»: contrato público vigente en España. Presupuesto, plazo de presentación, requisitos y contacto del órgano de contratación en {{brand}}.',
  'tender.seoDescDefault':
    'Licitaciones públicas de España: presupuestos, plazos de presentación y contactos de los órganos de contratación en {{brand}}.',
  'tender.facts.lead': 'Licitación pública «{{title}}» en España.',
  'tender.facts.region': 'Comunidad autónoma: {{region}}.',
  'tender.facts.budget': 'Presupuesto base de licitación: {{budget}} €.',
  'tender.facts.category': 'Categoría: {{category}}.',
  'tender.facts.deadline': 'Plazo de presentación hasta el {{deadline}}.',
  'tender.facts.published': 'Publicada el {{published}}.',
  'tender.facts.docs': 'La licitación incluye pliegos y documentación ({{count}} archivo(s)).',
  'tender.facts.label': 'La licitación en breve',
  'tender.outcome.label': 'Resultado',
  'tender.outcome.winner': 'Adjudicatario',
  'tender.outcome.winnerContact': 'Contacto del adjudicatario',
  'tender.outcome.winnerContactCapped':
    'Has alcanzado tu límite de hoy: 200 empresas. Hemos detectado una actividad inusual. Para quitar el límite, escribe a soporte',
  'tender.outcome.winnerContactCappedTelegram': 'Escribir a soporte',
  'companyData.link': '¿Es tu empresa? Ocultar el teléfono o corregir los datos',
  'support.companyData.topic': 'Asunto: datos de la empresa, NIF {{tin}}',
  'support.companyData.prefill': 'Datos de la empresa con NIF {{tin}}. ',
  'tender.buyer.profileLink': 'Perfil del órgano de contratación y todas sus licitaciones →',
  'tender.outcome.finalPrice': 'Importe de adjudicación',
  'tender.outcome.awardedAt': 'Fecha de adjudicación',
  'tender.outcome.failed': 'Licitación desierta',
  'tender.outcome.cancelled': 'Licitación anulada',
  'tender.outcome.noBids': 'No se presentaron ofertas',
  'tender.outcome.notAwarded': 'Sin adjudicatario: la licitación quedó desierta',
  'tender.outcome.locked': 'Ya está adjudicada. Adjudicatario, importe y contacto, en Pro',
  'tender.outcome.lockedCta': 'Hazte Pro',
  'tender.outcome.lockedContactOnly': 'Ya está adjudicada. Adjudicatario y contacto, en Pro',
  'tender.outcome.lockedPriceOnly': 'Ya está adjudicada. Adjudicatario e importe, en Pro',
  'tender.outcome.lockedWhoOnly': 'Ya está adjudicada. El adjudicatario, en Pro',
  'tender.outcome.lockedRow': 'Adjudicatario, importe y contacto, en Pro',
  'tender.outcome.lockedRowContact': 'Adjudicatario y su contacto, en Pro',
  'tender.outcome.lockedRowPrice': 'Adjudicatario e importe, en Pro',
  'tender.outcome.lockedRowWho': 'Adjudicatario, en Pro',
  'tender.nav.siblings': 'Otros lotes',
  'tender.siblings.headline': 'En el mismo anuncio hay {{count}} {{lots}} más por {{amount}}',
  'tender.siblings.headlineNoSum': 'En el mismo anuncio hay {{count}} {{lots}} más',
  'tender.siblings.hint':
    'Puedes presentar oferta a cualquiera de ellos, a cada lote por separado.',
  'tender.siblings.more': '+{{count}} más',
  'tender.contactInfo': 'Datos de contacto',
  'tender.contactDetails': 'Los datos de contacto están disponibles para usuarios registrados.',
  'tender.contactDetailsAuthed': 'Ábrelo para ver el contacto del órgano de contratación.',
  'tender.opening': 'Abriendo…',
  'tender.contactName': 'Persona de contacto',
  'tender.contactEmail': 'Email',
  'tender.contactPhone': 'Teléfono',
  'tender.contactCategory': 'Actividad',
  'tender.contactWebsite': 'Web',
  'tender.contactTelegram': 'Telegram',
  'tender.contactMapAddress': 'Dirección en el mapa',
  'tender.sourceLabel': 'Fuente original',
  'tender.openOriginal': 'Ver la licitación original',
  'tender.participateHint': 'Cómo presentar tu oferta y obtener el certificado digital',
  'tender.openOriginalCoop': 'Ver el original en la plataforma',
  'tender.documentsLabel': 'Pliegos y documentos',
  'tender.originalTitle': 'Título original: {{title}}',
  'tender.docsTeaser.tz': 'Pliego de prescripciones técnicas (PPT)',
  'tender.docsTeaser.contract': 'Pliego de cláusulas administrativas particulares (PCAP)',
  'tender.docsTeaser.spec': 'Anexos y especificaciones',
  // es (owner, 2026-10-20): the pliegos are Pro, as on UZ.
  'tender.docsTeaser.cta': 'Desbloquear los pliegos',
  'tender.docsTeaser.meteredCta': 'Documentos en la plataforma: abrir enlace (1 de {{count}})',
  'tender.docsTeaser.meteredHint':
    'Los archivos están en la plataforma de origen. Al abrirla tienes el enlace al original y el contacto del órgano de contratación: lo mismo que el botón de abajo.',
  'tender.docsTeaser.meteredOpenedCta': 'Documentos en la plataforma: ver el original',
  'tender.procedureLabel': 'Procedimiento',
  'tender.procedure.method': 'Tipo de procedimiento',
  'tender.procedure.obligationTerm': 'Plazo de ejecución',
  'tender.procedure.serviceType': 'Tipo de trabajos',
  'tender.procedure.financialSource': 'Financiación',
  'tender.procedure.address': 'Dirección',
  'tender.procedure.deliveryAddress': 'Lugar de entrega',
  'tender.procedure.decree': 'Fundamento legal',
  'tender.procedure.discussionStart': 'Inicio del periodo de consultas',
  'tender.procedure.discussionEnd': 'Fin del periodo de consultas',
  'tender.procedure.published': 'Fecha de publicación',
  'tender.procedure.positions': 'Partidas',
  'tender.procedure.positionsHint':
    'Cuántos bienes, obras o servicios distintos incluye este lote. El órgano de contratación los compra en una sola licitación y la oferta se presenta al lote completo.',
  'tender.procedure.docsDeadline': 'Plazo para preguntas y aclaraciones',
  'tender.procedure.opening': 'Apertura de ofertas',
  'tender.procedure.announcementName': 'Anuncio',
  'tender.procedure.announcementNumber': 'Número de expediente',
  'tender.procedure.subjectKind': 'Tipo de contrato',
  'tender.procedure.planType': 'Tipo de partida del plan',
  'tender.procedure.organizer': 'Organizador',
  'tender.procedure.representative': 'Representante del órgano de contratación',
  'tender.procedure.bidStart': 'Inicio de presentación de ofertas',
  'tender.procedure.vatNote': 'Características',
  'tender.procedure.commission': 'Mesa de contratación',
  'tender.procedure.sourceUpdated': 'Última actualización en la fuente',
  'tender.contactNone': 'Esta licitación no indica datos de contacto.',
  // es (owner, 2026-10-20): the órgano's contact is Pro, as on UZ.
  'tender.insufficientAllowance':
    'Has alcanzado el límite de consultas de contacto. Vuelve a intentarlo más tarde.',
  'tender.nicheRequired': 'Los contactos de los órganos de contratación están en el plan Pro.',
  'tender.proRequired':
    'Pásate a Pro para ver el contacto del órgano de contratación de cada licitación.',
  'tender.revealError': 'No se ha podido mostrar el contacto. Inténtalo más tarde.',
  'tender.upgradeCta': 'Ver planes',
  // Region
  'region.backToCatalog': '← Volver al catálogo',
  'region.title': 'Licitaciones en {{region}}',
  'region.loading': 'Cargando…',
  'region.found': '{{count}} licitaciones encontradas',
  'region.seoTitle': 'Licitaciones y contratos públicos en {{region}}',
  'region.seoTitlePaged': 'Licitaciones en {{region}}, página {{page}}',
  'region.seoDesc':
    'Licitaciones públicas vigentes en {{region}}: {{count}} abiertas. Presupuestos, plazos de presentación y contactos de los órganos de contratación en {{brand}}.',
  'region.seoDescPaged':
    'Licitaciones públicas en {{region}}, página {{page}} de {{totalPages}}: {{count}} abiertas en total. Presupuestos, plazos y compradores en {{brand}}.',
  'region.intro':
    'Licitaciones y contratos públicos en {{region}}: ahora hay {{count}} abiertas. De cada una tienes el presupuesto, el plazo de presentación y un resumen en español. Filtra por categoría, guarda la búsqueda y recibe las nuevas licitaciones por email o en Telegram.',
  // Category
  'category.backToCatalog': '← Volver al catálogo',
  'category.title': 'Licitaciones: {{category}}',
  'category.loading': 'Cargando…',
  'category.found': '{{count}} licitaciones encontradas',
  'category.topBuyers': 'Principales compradores de esta categoría',
  'category.seoTitle': 'Licitaciones y contratos: {{category}}',
  'category.seoTitlePaged': 'Licitaciones: {{category}}, página {{page}}',
  'category.seoDesc':
    'Licitaciones públicas de «{{category}}» en España: {{count}} abiertas. Presupuestos, plazos y compradores en {{brand}}.',
  'category.seoDescPaged':
    'Licitaciones de «{{category}}» en España, página {{page}} de {{totalPages}}: {{count}} abiertas en total. Presupuestos, plazos y compradores en {{brand}}.',
  'category.intro':
    '«{{category}}» es uno de los sectores de la contratación pública en España. Ahora el catálogo tiene {{count}} licitaciones vigentes de esta categoría: de cada una se indica el presupuesto base, el plazo de presentación y un resumen en español. Elige la comunidad autónoma en el catálogo, guarda la búsqueda y recibe las nuevas licitaciones por email o en Telegram.',
  // Guide
  'guide.backToCatalog': '← Volver al catálogo',
  'guide.seriesHeading': 'Licitar sin intermediarios: paso a paso',
  'guide.articlesHeading': 'Más temas para licitar',
  'guide.nicheGuidesHeading': 'Guías por sector',
  'guide.answersHeading': 'Respuestas breves',
  // Closed procurements read end to end (content package 2026-10-22).
  'guide.casesHeading': 'Casos reales',
  'guide.relatedHeading': 'Más sobre el tema',
  'guide.allArticles': 'Todos los artículos de la guía',
  // Login
  'login.title': 'Iniciar sesión',
  'login.subtitle': 'Te damos la bienvenida a {{brand}}.',
  'checkout.identify.emailHint': 'Te enviaremos ahí el acceso a la suscripción.',
  'checkout.identify.didYouMean': '¿Querías decir {{email}}?',
  'checkout.identify.emailExample': 'Por ejemplo: nombre@gmail.com',
  'login.telegramWhy': 'Acceso rápido con Telegram, igual que en el registro.',
  'login.email': 'Email',
  'login.password': 'Contraseña',
  'login.signingIn': 'Entrando…',
  'login.signIn': 'Entrar',
  'login.continue': 'Continuar',
  'login.codeTitle': 'Introduce el código',
  'login.codePrefilled': 'Hemos añadido el código del email',
  'login.subtitleFor': 'Acceso a tu cuenta',
  'login.or': 'o',
  'login.noAccount': '¿No tienes cuenta?',
  'login.signUp': 'Crear cuenta',
  // Register
  'register.title': 'Registro',
  'register.subtitle':
    'Gratis y sin tarjeta: guarda tus búsquedas y recibe las nuevas licitaciones por email.',
  'register.consentPrefix': 'Al crear la cuenta, aceptas',
  'register.pendingSearch': 'Al registrarte guardaremos la búsqueda «{{name}}»',
  'register.name': 'Nombre',
  'register.phone': 'Teléfono (opcional)',
  'register.phonePlaceholder': '+34 612 345 678',
  'register.phoneInvalid': 'Introduce un número válido, por ejemplo +34 612 345 678',
  'register.emailRequired': 'Introduce tu email',
  'register.codeIncomplete': 'Introduce el código de 6 cifras',
  'register.tin': '{{tin}}',
  'register.tinOptional': 'Opcional',
  'register.tinHint':
    '{{tinDigits}} caracteres: te mostraremos las licitaciones que has ganado (opcional)',
  'register.tinInvalid': 'El {{tin}} debe tener exactamente {{tinDigits}} caracteres',
  'register.email': 'Email',
  'register.sendCode': 'Recibir código',
  'register.sendingCode': 'Enviando…',
  'register.codeSentTo': 'Hemos enviado el código a',
  'register.code': 'Código del email',
  'register.codeHint': 'El código vale 10 minutos. ¿No te llega? Revisa la carpeta de spam.',
  'auth.otp.resend': 'Reenviar el código',
  'auth.otp.resendIn': 'Podrás reenviar el código dentro de {{seconds}} s.',
  'auth.otp.notArrived': '¿No te ha llegado el email con el código?',
  'register.emailDidYouMean': '¿Querías decir',
  'register.icloudSpam':
    'iCloud Mail suele mandar nuestros emails a «Correo no deseado»: revisa esa carpeta. O entra con Telegram más arriba, es más rápido.',
  'register.changeEmail': 'Usar otra dirección',
  'register.editEmail': 'Cambiar',
  'register.telegramWhy':
    'Las alertas de licitaciones llegan a Telegram: así quedan activadas desde el principio.',

  'register.password': 'Contraseña',
  'register.passwordHint': 'Mínimo 8 caracteres',
  'register.creating': 'Creando…',
  'register.createAccount': 'Crear cuenta',
  'register.haveAccount': '¿Ya tienes cuenta?',
  'register.signIn': 'Entrar',
  'auth.error.emailTaken': 'Este email ya está registrado. Inicia sesión.',
  'auth.error.invalidCredentials': 'Email o contraseña incorrectos.',
  'auth.error.generic': 'Algo ha fallado. Inténtalo de nuevo.',
  'auth.error.emailSyntax':
    'Revisa la dirección de email: parece que tiene una errata (por ejemplo, falta .com o .es)',
  'onboarding.banner.title': 'Configuración del perfil: {{done}} de {{total}}',
  'onboarding.banner.titleShort': 'Perfil: {{done}} de {{total}}',
  'onboarding.banner.show': 'Mostrar',
  'onboarding.banner.hide': 'Ocultar',
  'onboarding.banner.go': 'Activar',
  'onboarding.banner.search': 'Búsqueda guardada',
  'onboarding.banner.telegram': 'Alertas',
  'onboarding.banner.inn': '{{tin}} de la empresa',
  'onboarding.banner.email': 'Confirmar el email',
  'onboarding.banner.activate': 'Añadir',
  'onboarding.banner.noInn': 'Más tarde',
  'onboarding.banner.emailWhy':
    'Confirma tu email: así proteges tu cuenta y recibes alertas de nuevas licitaciones',
  'onboarding.banner.emailWhyShort': 'Para recibir las selecciones por email.',
  'onboarding.verifyEmail.send': 'Enviar código',
  'onboarding.verifyEmail.verify': 'Confirmar',
  'onboarding.inn.title': '¿Nombre o {{tin}} de tu empresa?',
  'onboarding.inn.subtitle':
    'Te mostraremos lo que tu empresa ya ha ganado y cuántas licitaciones hay abiertas ahora en tu sector.',
  'onboarding.inn.skip': 'Omitir',
  'onboarding.inn.digitsHint': 'Deben ser exactamente {{digits}} caracteres',
  'onboarding.inn.next': 'Siguiente',
  // Company step, Spain (`features.aiCompanyStep`, owner 22.10): the codes of the NIF's
  // awards are OFFERED for the alert — one line and a button, never added silently.
  'onboarding.companyCodes.offer':
    'Añade a tu alerta los códigos CPV de tus contratos anteriores: {{names}}.',
  'onboarding.companyCodes.more': '{{n}} más',
  'onboarding.companyCodes.add': 'Añadir a mi alerta',
  'onboarding.companyCodes.adding': 'Añadiendo…',
  'onboarding.companyCodes.added': 'Añadidos a tu alerta.',
  // QA 22.10 Н2: an alert holds 8 CPV codes at most; the most frequent ones go in.
  'onboarding.companyCodes.addedSome':
    'Añadidos {{added}} de {{total}} códigos; el límite son {{max}}.',
  'onboarding.companyCodes.error': 'No se han podido añadir. Inténtalo de nuevo.',
  'auth.error.passwordRequired': 'Introduce la contraseña',
  'auth.error.invalidCode':
    'El código no es válido: puede que haya caducado o que lo haya sustituido uno más reciente. Pide un código nuevo.',
  'auth.error.invalidPhone': 'Revisa el número de teléfono.',
  // Dashboard
  'dashboard.title': '{{brand}}: panel',
  'dashboard.signedInAs': 'Has iniciado sesión como {{email}}.',
  'dashboard.signOut': 'Salir',
  // SEO
  'seo.landing.title': '{{brand}}: encuentra oportunidades de negocio',
  'seo.landing.description': 'Consulta las licitaciones públicas de toda España.',
  'seo.catalog.title': 'Licitaciones y contratación pública en España',
  'seo.catalog.description':
    'Catálogo de licitaciones públicas de España: contratos vigentes, resúmenes en español, filtros por comunidad autónoma y categoría, y alertas por email o en Telegram, en {{brand}}.',
  'seo.catalog.titlePaged': 'Licitaciones públicas en España, página {{page}}',
  'seo.results.title': 'Adjudicaciones de contratos públicos en España: adjudicatarios e importes',
  'seo.results.titlePaged': 'Adjudicaciones en España, página {{page}}',
  'seo.results.description':
    'Licitaciones adjudicadas en España. Adjudicatario e importe, en Pro; filtros y búsquedas guardadas, gratis.',
  'results.page.h1': 'Adjudicaciones',
  'results.page.intro': 'Licitaciones adjudicadas en el último mes: quién ganó y por cuánto.',
  'results.card.title': '¿Qué ofreces?',
  'results.card.sub': 'Te mostramos quién ha ganado licitaciones así.',
  'results.card.placeholder': 'mobiliario, limpieza, bombas…',
  'results.card.inputLabel': 'Palabras, categorías y códigos CPV',
  'results.card.remove': 'Quitar «{{label}}»',
  'results.card.category': 'Categoría:',
  'results.card.ikpu': 'CPV',
  'results.suggest.word': 'Palabra:',
  'results.suggest.wordHint': 'en el título y la descripción',
  'results.suggest.foot':
    '↵ para añadir como palabra · la categoría es más precisa, la palabra más amplia',
  'results.refine': 'Afinar',
  'results.refine.empty': 'comunidad · importe · exclusiones',
  'results.refine.region': 'Comunidad autónoma',
  'results.refine.addRegion': '+ comunidad',
  'results.refine.sum': 'Importe del lote, €',
  'results.refine.from': 'desde',
  'results.refine.to': 'hasta',
  'results.refine.any': 'cualquiera',
  'results.refine.sumFrom': 'desde {{amount}}',
  'results.refine.sumTo': 'hasta {{amount}}',
  'results.refine.exclude': 'Excluir si contiene las palabras',
  'results.refine.excludePlaceholder': 'palabra…',
  'results.refine.excludeCount': 'sin {{count}} pal.',
  'results.refine.kind': 'Tipo de contrato',
  'results.refine.customer': 'Comprador',
  'results.refine.customerLabel': '{{tin}} del comprador',
  'results.kind.goods': 'Suministros',
  'results.kind.works': 'Obras',
  'results.kind.services': 'Servicios',
  'results.count.found': 'Se han encontrado {{count}} {{word}}',
  'results.count.period': 'en el último mes',
  'results.count.week': '{{count}} esta semana',
  'results.count.today': '{{count}} hoy',
  'results.count.loading': 'Contando resultados…',
  'results.save': 'Guardar búsqueda',
  'results.update': 'Actualizar búsqueda',
  'results.saved': 'Búsqueda guardada',
  'results.save.empty': 'Añade una palabra para poder guardar la búsqueda',
  'results.save.guest': 'Gratis: tendrás que registrarte',
  'results.save.note':
    'Una búsqueda de adjudicaciones por cuenta: si guardas otra, sustituye a esta',
  'results.save.error': 'No se ha podido guardar la búsqueda. Inténtalo de nuevo',
  'results.changed': 'Cambios: {{list}}. Actualiza la búsqueda para guardarlos',
  'results.updated': 'Actualizado: {{list}}',
  'results.created':
    'Guardada. Las alertas de nuevas adjudicaciones se configuran en los ajustes de alertas',
  'results.change.region': 'comunidad',
  'results.change.budget': 'importe',
  'results.change.exclude': 'exclusiones',
  'results.change.kind': 'tipo de contrato',
  'results.change.customer': 'comprador',
  'results.change.text': 'palabras',
  'results.change.category': 'categorías',
  'results.change.ikpu': 'códigos CPV',
  'results.change.name': 'nombre',
  // No «contactos»: PLACSP publishes no adjudicatario phone or e-mail (the
  // placsp adapter writes winners with none), so the static line names only the
  // award. The per-lot lock text says «y contacto» only when one is on file.
  'results.wall.line': 'Adjudicatarios, NIF e importes, en Pro',
  'results.wall.cta': 'Hazte Pro',
  'savedSearches.results.name': 'Adjudicaciones',
  'savedSearches.results.hint': 'Alerta cuando se adjudica una licitación parecida',
  'savedSearches.results.edit': 'Cambiar en la página de adjudicaciones',
  // ── App shell ──────────────────────────────────────────────────
  'shell.search.placeholder': 'Busca licitaciones por su contenido…',
  'shell.search.ai': 'IA',
  'shell.section.catalog': 'Catálogo',
  'shell.section.regions': 'Comunidades autónomas',
  'shell.section.categories': 'Categorías',
  'shell.showAll': 'Ver todas ({{count}})',
  'shell.showLess': 'Ver menos',
  'shell.section.savedSearches': 'Búsquedas guardadas',
  'shell.saved.guestCta': 'Guardar tu búsqueda',
  'shell.nav.allTenders': 'Licitaciones',
  'shell.nav.ifiProjects': 'Organismos internacionales',
  'shell.nav.supplyRequests': 'Solicitudes de oferta',
  'shell.nav.favorites': 'Favoritos',
  'favorites.title': 'Favoritos',
  'favorites.subtitle': 'Las licitaciones que has guardado para volver a ellas más tarde.',
  'favorites.empty.title': 'Aquí todavía no hay nada',
  'favorites.empty.desc': 'Pulsa ☆ en una licitación para añadirla a favoritos.',
  'favorites.empty.cta': 'Ir al catálogo',

  // ── My tenders (/app/my-tenders) ───────────────────────────────────────
  'myTenders.title': 'Mis licitaciones',
  'myTenders.subtitle':
    'Todas las licitaciones que encajan con tus búsquedas guardadas, las nuevas primero',
  'myTenders.chip.all': 'Todas',
  'myTenders.showClosed': 'Mostrar las cerradas',
  'myTenders.total': 'Total: {{count}}',
  'myTenders.bySearch': 'por la búsqueda: {{names}}',
  'myTenders.new': 'nueva',
  'myTenders.newCountAria': 'Mis licitaciones, coincidencias nuevas: {{count}}',
  'myTenders.empty.title': 'Todavía no hay coincidencias',
  'myTenders.empty.descOpen':
    'No hay licitaciones abiertas para tus búsquedas. Activa «Mostrar las cerradas» o cambia la búsqueda en la página «Alertas».',
  'myTenders.empty.desc':
    'Todavía no hay ninguna licitación para tus búsquedas. Cambia los criterios en la página «Alertas».',
  'shell.nav.alerts': 'Alertas',
  'shell.nav.menu': 'Menú',
  'shell.nav.menu.a11yDesc': 'Secciones del sitio, búsquedas guardadas y ajustes de la cuenta.',
  'shell.nav.openMenu': 'Abrir el menú',
  'shell.nav.closeMenu': 'Cerrar el menú',
  'shell.saved.empty': 'Todavía no tienes búsquedas guardadas',
  // The same button with nothing picked opens «¿Qué ofreces?» (`features.alertLanding`).
  'catalog.saveSearch': 'Crear alerta gratis',
  'catalog.saveSearchGuest': 'Crea tu alerta gratis: solo te pedimos el email',
  'saved.close': 'Cerrar',

  // ── Saved searches on /app/alerts ───────────────────────────────────
  'savedSearches.title': 'Búsquedas guardadas',
  'savedSearches.edit': 'Editar',
  'savedSearches.summary.from': 'desde {{amount}}',
  'savedSearches.summary.to': 'hasta {{amount}}',
  'savedSearches.summary.except': 'excepto: {{terms}}',
  'savedSearches.rate': '≈ {{count}} lotes al día',
  'savedSearches.rate.unknown': 'frecuencia aún sin calcular',
  'savedSearches.delete.title': 'Eliminar la búsqueda',
  'savedSearches.delete.confirm':
    '¿Eliminar la búsqueda «{{name}}»? Dejarás de recibir sus alertas.',
  'savedSearches.instant': 'Enviar al momento',
  'savedSearches.instant.hint':
    'Cada lote nuevo en un mensaje aparte, sin esperar al resumen. Solo para búsquedas precisas (hasta 3 lotes al día); como máximo 5 mensajes por hora.',
  'savedSearches.instant.no.broad':
    'Solo para búsquedas acotadas (palabra, CPV, comunidad autónoma, sector…)',
  'savedSearches.instant.no.rate':
    'Esta búsqueda da ≈{{count}} al día: demasiados para mensajes sueltos',
  'savedSearches.instant.no.young': 'No hemos podido estimar la frecuencia. Inténtalo más tarde',
  'savedSearches.instant.no.plan': 'Disponible en Pro',
  'savedSearches.instant.rejected':
    'No se ha podido cambiar: la búsqueda ya no admite mensajes sueltos. Recarga la página.',
  'saved.deleteYes': 'Sí',
  'saved.delete': 'Eliminar',
  'saved.edit': 'Cambiar',
  'saved.matches': 'Licitaciones que encajan ahora: {{n}}',
  'saved.rate': '≈{{n}} a la semana con este filtro',
  'saved.minBudgetLabel': 'Presupuesto desde, €',
  'saved.minBudgetPlaceholder': '50.000',
  'saved.maxBudgetLabel': 'Presupuesto hasta, €',
  'saved.maxBudgetPlaceholder': '1.000.000',
  'saved.budgetRangeError': 'El presupuesto «hasta» no puede ser menor que «desde».',
  'saved.learnedLabel': 'Excluido por tus 👎 (pulsa para recuperarlo)',
  'saved.learnedRemove': 'Recuperar',
  'saved.excludeTermsLabel': 'No enviar licitaciones con las palabras',
  'saved.excludeTermsPlaceholder': 'palabras separadas por comas',
  'saved.excludeTermsHint':
    'Una licitación con cualquiera de estas palabras no llegará en las alertas.',
  'tagInput.remove': 'Quitar «{{term}}»',
  'tagInput.max': 'Máximo {{count}}',
  'tagInput.tooLong':
    'Demasiado largo: {{count}} caracteres, el máximo es {{max}}. Déjalo en una o dos palabras.',
  'saved.matchesNone':
    'Ahora no hay licitaciones abiertas con estos filtros: no recibirás alertas hasta que aparezca alguna. Prueba a ampliar el filtro.',
  'saved.nameLabel': 'Nombre',
  'saved.defaultName': 'Mi búsqueda',
  'saved.save': 'Guardar',
  'saved.cancel': 'Cancelar',
  'saved.edit.title': 'Ajustar la búsqueda',
  'saved.edit.a11yDesc': 'Nombre, categorías, exclusiones y presupuesto mínimo de la búsqueda.',
  'saved.edit.saving': 'Guardando…',
  'saved.edit.error': 'No se ha podido guardar',
  'saved.edit.errorSubject': 'Deja al menos uno: categoría, CPV, sector, comprador o palabra.',
  'saved.edit.errorInvalid': 'No se ha podido guardar: revisa los campos e inténtalo de nuevo.',
  'saved.edit.errorTerms':
    'Hay demasiadas palabras o alguna es demasiado larga. Quita las que sobren y vuelve a guardar.',
  'saved.saved': 'Guardado',
  'saved.toast': 'Búsqueda guardada',
  'saved.tender.add': 'A favoritos',
  'saved.tender.remove': 'Quitar de favoritos',
  'saved.limitReached':
    'En el plan gratuito puedes guardar hasta {{n}} búsquedas. Hazte Pro para tener búsquedas ilimitadas.',
  'saved.errorGeneric': 'No se ha podido guardar la búsqueda. Inténtalo de nuevo.',
  'saved.emptyCriteria':
    'Añade al menos un filtro: categoría, comunidad autónoma, palabra clave o presupuesto.',
  'alert.picker.title': '¿Qué alerta quieres configurar?',
  'alert.picker.createNew': 'Crear una nueva',
  'saved.tuneAlert': 'Configurar alertas',
  'saved.deleteAlert': 'Eliminar la alerta',
  'saved.deleteConfirm': 'Sí, eliminar',
  'saved.deleteWarning':
    'Dejarás de recibir las alertas de esta búsqueda. Puedes crear otra desde el catálogo.',
  'shell.user.signIn': 'Entrar',
  'shell.user.guest': 'Invitado',
  'shell.user.notSignedIn': 'No has iniciado sesión',
  'shell.user.freePlan': 'Plan gratuito',
  'shell.user.paidPlan': 'Plan {{plan}}',
  'shell.upgrade.label': 'Pasar a Pro',
  // es (owner, 2026-10-20): the UZ Pro set — contacts, pliegos, analytics. Pitched
  // market-first since review 21.10 «first hour», item 3: PLACSP gives the contact
  // and the pliegos away, not who wins and for how much.
  'shell.upgrade.desc': 'Quién gana, por cuánto y contra quién compites; alertas sin límite',
  // ── Connect Telegram for alerts ──────────────────────────────────
  'telegram.connect.title': 'Conecta Telegram para recibir alertas',
  'telegram.connect.desc':
    'Recibe las nuevas licitaciones de tu sector directamente en Telegram. Pulsa el botón de abajo y permite que el bot te escriba.',
  'telegram.connect.benefit1': 'Alertas al momento de tus búsquedas guardadas',
  'telegram.connect.benefit2': 'Avisos de licitaciones que encajan con tu empresa',
  'telegram.connect.cta': 'Conectar Telegram',
  'telegram.bot.cta': 'Entrar con Telegram',
  'telegram.bot.waiting': 'Pulsa Start en Telegram y entrarás automáticamente',
  'telegram.bot.waitingConnect': 'Pulsa Start en Telegram y se conectará automáticamente',
  'telegram.bot.waitingDesktop': 'Abre Telegram y pulsa Start. Esperando…',
  'telegram.bot.reopen': 'Volver a abrir Telegram',
  'telegram.bot.inAppHint':
    '¿No se ha abierto Telegram? En {{app}} pulsa ⋯ arriba a la derecha → «Abrir en el navegador».',
  'telegram.bot.notOpenedHint':
    '¿No se ha abierto Telegram? Copia el enlace y ábrelo en el navegador.',
  'telegram.bot.copyLink': 'Copiar el enlace',
  'telegram.bot.copied': 'Enlace copiado',
  'telegram.bot.expired': 'No ha funcionado. Vuelve a pulsar.',
  'telegram.bot.connected': 'Telegram conectado',
  'telegram.bot.failed':
    'No hemos podido preparar el acceso con Telegram. Vuelve a pulsar el botón.',
  'telegram.magic.signingIn': 'Entrando…',
  'telegram.magic.expired':
    'El enlace de acceso ha caducado o ya se ha usado. Vuelve a entrar con Telegram: es un solo botón.',
  'telegram.magic.toLogin': 'Entrar',
  'telegram.magic.toSite': 'Ir al sitio',
  'telegram.magic.checking': 'Comprobando el enlace…',
  'telegram.magic.confirmAs': '¿Entrar como {{name}}?',
  'telegram.magic.confirm': '¿Entrar con Telegram?',
  'telegram.magic.confirmHint':
    'Acceso con el enlace de nuestro bot de Telegram. Si este Telegram no es tuyo, pulsa «No soy yo».',
  'telegram.magic.signIn': 'Entrar',
  'telegram.magic.notMe': 'No soy yo',
  'telegram.magic.linking': 'Conectando Telegram…',
  'telegram.magic.mismatch':
    'Este enlace es de otra cuenta. Has iniciado sesión con la tuya y no ha cambiado nada en ella.',
  'telegram.connect.mismatch':
    'Este acceso con Telegram se inició desde otra cuenta y no ha cambiado nada. Vuelve a pulsar el botón.',
  'telegram.connect.error': 'No se ha podido conectar Telegram. Inténtalo de nuevo.',
  'telegram.connect.taken':
    'Este Telegram ya está vinculado a otra cuenta de {{brand}}. Entra en esa cuenta o desvincula primero Telegram de ella. Para cambiar de cuenta de Telegram, pulsa «Salir» en el propio botón de Telegram (muestra el nombre de quien ha entrado) y entra con la tuya.',
  'telegram.connect.hasTeam':
    'Este Telegram ya está vinculado a una cuenta que forma parte de un equipo. Esa cuenta no se puede fusionar automáticamente: escríbenos a {{brandEmail}}.',
  'telegram.connect.hasAssets':
    'Este Telegram está vinculado a una cuenta con suscripción, pagos u otros datos propios. Esa cuenta no se puede fusionar automáticamente: entra en ella o escríbenos a {{brandEmail}}.',
  'telegram.status.connected': 'Telegram conectado',
  'telegram.status.desc': 'Las alertas de tus búsquedas guardadas llegan a Telegram.',
  'telegram.disconnect.cta': 'Desconectar',
  'telegram.disconnect.pending': 'Desconectando…',
  'telegram.disconnect.confirm':
    '¿Desconectar Telegram? Dejarás de recibir alertas hasta que lo vuelvas a conectar.',
  // ── Tender row / list ──────────────────────────────────────────
  'tender.status.active': 'Abierta',
  'tender.publishedShort': 'publicada',
  'tender.status.closing': 'Cierra pronto',
  'tender.status.closed': 'Cerrada',
  'tender.ai': 'IA',
  'tender.budget.unit': '€',
  'teaser.srLocked': 'Disponible en Pro',
  'teaser.openPlans': '{{card}}: disponible en Pro, ver planes',
  'teaser.companiesCaption': 'Cuotas, fechas y la lista completa, en Pro',
  'tender.budget.undisclosed': 'Sin presupuesto publicado',
  // A PLACSP lot whose budget is published only for the whole expediente.
  'tender.budget.expedienteTotal': 'Importe total del expediente',
  'tender.notice.ifb': 'Invitación a licitar',
  'tender.notice.reoi': 'Solicitud de expresiones de interés',
  'tender.notice.gpn': 'Anuncio general',
  'tender.notice.advance': 'Anuncio previo',
  'tender.notice.firm': 'Selección de consultores',
  // PLACSP feed 1403 — the card's and the lot header's stage label.
  'tender.notice.consultation': 'Consulta preliminar del mercado',
  'tender.bidSecurity': 'Garantía provisional',
  'tender.deadline.closes': 'Cierre',
  'tender.deadline.tba': 'Por confirmar',
  'tender.deadline.announcement': 'Pendiente de convocatoria',
  'tender.deadline.announcementNote':
    'Es un anuncio previo: la licitación está anunciada y prevista, pero el plazo de presentación todavía no se ha abierto. Añádela a favoritos para no perderla.',
  // What a consulta preliminar del mercado is, two lines under the lot facts.
  'tender.consultationNote':
    'Es una consulta preliminar del mercado: el órgano de contratación pregunta a las empresas antes de convocar la licitación. Responder es gratis y no te compromete, y tus aportaciones pueden dar forma a los pliegos.',
  'tender.similar.title': 'Licitaciones parecidas',
  'tender.closedNoOutcome.title': 'El plazo de presentación terminó el {{date}}',
  'tender.closedNoOutcome.titleNoDate': 'El plazo de presentación ha terminado',
  'tender.closedNoOutcome.body': 'Todavía no se ha publicado la adjudicación.',
  'tender.similarLive.title': 'Lotes abiertos parecidos',
  'tender.similarLive.follow': 'Seguir licitaciones así',
  'list.col.tender': 'Licitación',
  'list.col.budget': 'Presupuesto',
  'list.positions.label': 'Partidas',
  'list.positions.qty': 'Cantidad',
  'list.positions.more': '… {{n}} en total',
  'list.col.noticeType': 'Tipo de anuncio',
  'list.col.deadline': 'Plazo',
  'list.col.awarded': 'Adjudicación',
  'landing.lotCard.contactLabel': 'Contacto del comprador',
  'landing.lotCard.lockLabel': 'en Pro',
  'landing.lotCard.activeLabel': 'Abierta',
  'landing.lotCard.docsLabel': 'Pliegos (PCAP y PPT)',
  'landing.lotCard.bidSecurityLabel': 'Garantía',
  // «cierra en», not «quedan»: the verb then agrees with 1 too («quedan 1 día», QA 20.10).
  'landing.lotCard.daysLeft': 'cierra en {{n}} {{word}}',
  'landing.nicheSwitcher.emptyLabel':
    'Ahora no hay lotes abiertos en este sector: abre la categoría completa.',
  'landing.illustration.chatLabel': 'Ejemplo de alertas en Telegram',
  'landing.illustration.rangeAriaLabel':
    '{{label}}: de {{min}} a {{max}}, esta partida está en el {{pct}} %',
  'search.empty.title': 'Ahora no hay licitaciones así',
  'search.empty.body':
    'Ahora mismo no hay ningún lote así en el catálogo. Suscríbete y te avisaremos por email o en Telegram el mismo día en que se publique.',
  // Only when a filter (category / CPV / sector / region / kind) narrowed the
  // search AND the unfiltered catalogue has lots for the same word — the count
  // is live, from one `limit=1` request. {{word}} is «licitación» /
  // «licitaciones» (no-results-alert.tsx, es branch).
  'search.empty.withoutFilter':
    'Con el filtro elegido no hay nada para «{{q}}». En todo el catálogo hay {{count}} {{word}}.',
  'search.empty.withoutFilterLink': 'Ver sin filtro →',
  'niche.hint.applied':
    'Parece que buscas el sector «{{niche}}»: hemos añadido sus lotes a los resultados.',
  'niche.hint.suggest': 'Parece que buscas el sector «{{niche}}».',
  'niche.hint.open': 'Abrir el sector →',
  'niche.hint.ambiguous': 'Puede que busques uno de estos sectores:',
  'catalog.lotNumberMatch.one': 'Encontrado el lote n.º {{n}}',
  'catalog.lotNumberMatch.many': 'Encontrados lotes con el n.º {{n}}',
  'catalog.cpvCodeMatch': 'Licitaciones con CPV {{codes}} y todos sus subcódigos',
  // Negative QA 22.10 (A2.7): `?page=999` — past the last page, not «no matches».
  'catalog.pastLastPage.title': 'No hay más resultados',
  'catalog.pastLastPage.desc': 'La última página es la {{pages}}.',
  'catalog.pastLastPage.first': 'Ir a la página 1',
  'org.hint.intro': '«{{q}}» es el {{tin}} de una organización. Abrir su perfil:',
  'org.hint.buyer': '{{name}}: comprador',
  'org.hint.supplier': '{{name}}: empresa licitadora',
  'list.density.normal': 'Normal',
  'list.density.compact': 'Compacta',
  'list.empty': 'No hay licitaciones que cumplan los filtros actuales.',
  'list.bidPackageReady': 'Oferta preparada',
  'ifi.badge': 'Org. int.',
  'supply.badge': 'Solicitud',
  'scope.chips.aria': 'Filtrar por sección del catálogo',
  'scope.chips.onlyOther':
    'No hay coincidencias en las licitaciones públicas con presupuesto: abajo tienes lotes de otras secciones.',
  'scope.chips.didYouMean': 'Quizá quisiste decir:',
  'ifi.page.h1': 'Proyectos de organismos internacionales',
  'ifi.page.intro':
    'Contratos de proyectos financiados por instituciones financieras internacionales. El comprador es un organismo público y la financiación es bancaria, por eso el presupuesto se publica en la moneda del proyecto.',
  'ifi.projects.title': 'Proyectos por organismo',
  'ifi.projects.emptyForSource':
    'Esta institución no publica el organismo ejecutor, así que este desglose está vacío.',
  'ifi.projects.subtitle':
    'Proyectos de instituciones internacionales por ministerio y organismo comprador.',
  'ifi.source.aria': 'Institución',
  'ifi.source.all': 'Todas',
  'ifi.source.worldbank': 'Banco Mundial',
  'ifi.source.adb': 'BAsD',
  'ifi.source.aiib': 'AIIB',
  'ifi.source.undp': 'PNUD',
  'ifi.source.ungm': 'UNGM',
  'ifi.source.isdb': 'BIsD',
  'ifi.source.ebrd': 'BERD',
  'ifi.source.uzrd': 'UzRD',
  'ifi.source.osce': 'OSCE',
  'ifi.source.ariaLabel': '{{label}}, {{count}} licitaciones',
  'ifi.seo.title': 'Licitaciones del Banco Mundial, el BAsD y la ONU en España',
  'ifi.seo.description':
    'Licitaciones abiertas de proyectos de instituciones financieras internacionales en España: Banco Mundial, bancos de desarrollo y agencias de la ONU. Plazos, documentos y requisitos.',
  // Supply-request leads: no such source in Spain (supplyScope is off); kept neutral.
  'supply.page.h1': 'Solicitudes de oferta',
  'supply.page.intro':
    'Solicitudes de suministro sin presupuesto anunciado: están en una sección aparte y no forman parte del catálogo principal.',
  'supply.seo.title': 'Solicitudes de oferta sin presupuesto anunciado',
  'supply.seo.description':
    'Solicitudes de suministro de bienes y servicios sin presupuesto indicado.',
  // ── Filter bar ─────────────────────────────────────────────────
  'filter.label': 'Filtro:',
  'filter.region': 'Comunidad',
  'filter.customer': 'Comprador',
  'filter.category': 'Categoría',
  'filter.ikpu.label': 'O por código CPV',
  'filter.ikpu.allUnder': 'Todos los códigos {{code}}',
  'filter.ikpu.searchWords': 'No existe ese código: buscar «{{q}}» como palabra en los lotes',
  'filter.ikpu.noCodeHint':
    'No hay ningún código con esas palabras. Escribe en una palabra lo que ofreces, por ejemplo «mobiliario» o «publicidad»',
  'filter.ikpu.countOfParent': '{{n}} de {{total}}',
  'filter.ikpu.expand': 'Desplegar la sección',
  'filter.ikpu.collapse': 'Plegar la sección',
  'filter.more.categories': 'Ver {{count}} categorías más',
  'filter.chip.remove': 'Quitar: {{name}}',
  'filter.ikpu.pickedInside': 'Seleccionados dentro: {{count}}',
  'filter.more.niches': 'Ver {{count}} sectores más',
  'filter.more.ikpu': 'Ver más códigos',
  'filter.status': 'Estado',
  'filter.all': 'Todas',
  'filter.selectCategory': 'Elegir categoría',
  'filter.niche': 'Categoría o producto',
  'filter.niche.search': 'Buscar: categoría o producto…',
  'filter.multi.selectedCount': 'Seleccionados: {{count}}',
  'niche.section': 'Sectores',
  'niche.badge': 'sector',
  'filter.status.active': 'Abiertas',
  'filter.status.closing': 'Cierran pronto',
  'filter.status.closed': 'Cerradas',
  'filter.status.all': 'Todas',
  'filter.clear': 'Borrar filtros',
  'result.count': '{{count}} {{word}}',
  'result.countAtLeast': '{{count}} {{word}}',
  'result.countOf': '{{count}} de {{total}} {{word}}',
  'result.matching': 'con los filtros actuales',
  'result.hint.gov':
    'Solo se muestran las licitaciones públicas abiertas de España: vigentes y a punto de cerrar. Las cerradas y vencidas no cuentan. El catálogo se actualiza automáticamente.',
  'result.hint.ifi':
    'Proyectos financiados por instituciones internacionales (Banco Mundial, BAsD, etc.). No todos publican el presupuesto en euros, por eso el recuento incluye anuncios abiertos sin importe.',
  'result.hint.corporate':
    'Compras propias de empresas privadas. Solo se muestran los anuncios abiertos de sus portales.',
  // ── Catalog header stat chips ──────────────────────────────────
  'catalog.chips.active': '{{count}} abiertas',
  'catalog.chips.closingWeek': '{{count}} cierran pronto',
  // {{word}} is «nueva» / «nuevas» (catalog.tsx, es branch).
  'catalog.chips.newToday': '{{count}} {{word}}',
  // {{lots}} is «lote abierto» / «lotes abiertos» (MySelectionLine, es branch).
  'catalog.mySelection': 'Tu selección: {{count}} {{lots}}',
  'catalog.alertFilter.label': 'Lotes de tu alerta',
  'catalog.alertFilter.clear': 'Ver todo el catálogo',
  'catalog.searchScope.label': 'Resultados para «{{q}}» con los filtros activos',
  'catalog.searchScope.all': 'Buscar en todo el catálogo',
  // «Tu selección», second line (`messaging.emailWithoutConfirmation`, AlertWhenNote).
  'catalog.alertWhen': 'Te escribiremos a {{email}} cuando salga una nueva.',
  'catalog.alertWhenRate':
    'Te escribiremos a {{email}} cuando salga una nueva (≈ {{n}} a la semana).',
  // `features.alertLanding` (review 21.10 «first hour», item 1): the first screen says what
  // the ad says («Crea tu alerta gratis… por email… Gratis y sin tarjeta»).
  'catalog.spotlight.title':
    'Dinos qué vendes y te avisamos gratis por email de cada licitación nueva',
  'catalog.spotlight.noCard': 'Sin tarjeta',
  'catalog.spotlight.openNow': '{{count}} abiertas ahora',
  'result.updated': 'Se actualiza',
  'tgBanner.title': 'Recibe licitaciones en Telegram',
  'tgBanner.desc': 'Alertas de tus búsquedas guardadas',
  'tgBanner.dismiss': 'Ocultar',
  'channelCta.title': 'Grandes licitaciones en Telegram',
  'channelCta.desc': 'Los adjudicatarios de la semana y los lotes más grandes, cada día',
  'channelCta.button': 'Suscribirse',
  // ── Pagination ─────────────────────────────────────────────────
  'pagination.showing': 'Mostrando {{from}}–{{to}} de {{total}}',
  'pagination.prev': 'Anterior',
  'pagination.next': 'Siguiente',
  // ── Upgrade banner / plans ─────────────────────────────────────
  // es (owner, 2026-10-20): Pro is the UZ set — contacts, pliegos, adjudicaciones,
  // analytics, Excel, unlimited alerts. No AI read of the pliegos is sold.
  'plans.eyebrow': 'Planes Pro',
  'plans.title': 'Mira quién gana y por cuánto antes de ofertar',
  'plans.subtitle':
    'Gratis: buscar, leer y alertas (hasta 3 búsquedas). Pro: quién gana, por cuánto y contra quién compites, y alertas sin límite.',
  'plans.cta': 'Ver planes',
  'plans.dismiss': 'Más tarde',
  // ── Plans / pricing page ───────────────────────────────────────
  'plans.pageTitle': 'Planes',
  'plans.seoTitle': 'Planes y precios: contactos de compradores y pliegos',
  'plans.seoDesc':
    'Planes de {{brand}}: Pro con los contactos de los órganos de contratación, los pliegos, los adjudicatarios e importes, alertas ilimitadas y exportación a Excel de la contratación pública en España. Catálogo, resúmenes y requisitos, gratis.',
  'plans.pageLead':
    'Leer y entender licitaciones es gratis. Pro te dice quién gana, por cuánto y contra quién compites, con alertas sin límite: lo que necesitas para decidir tu oferta. También abre el contacto del órgano de contratación y los pliegos.',
  // Spain only (`FREE_LIMITS_LINE` in routes/plans.tsx): the free plan's limits, the same
  // facts as «Condiciones» (Plan gratuito) and Lucía's GRATIS block. 3 = FREE_SAVED_SEARCH_LIMIT.
  'plans.freeLimits':
    'Gratis y sin límite de tiempo: el catálogo con el resumen IA y los requisitos de cada licitación, y hasta 3 búsquedas guardadas con alertas por email. Los contactos de los órganos de contratación y los pliegos, solo con Pro.',
  'billing.autoRenew.title': 'Renovación automática',
  'billing.subscription.title': 'Tu suscripción',
  'billing.autoRenew.card': 'Tarjeta en Stripe: {{pan}}',
  'billing.autoRenew.toggle': 'Renovar la suscripción automáticamente',
  'billing.autoRenew.note':
    'Stripe cobra el día en que termina el periodo, sin pedir confirmación. Puedes desactivarlo cuando quieras.',
  'billing.autoRenew.retention':
    'Mientras la renovación esté activa, no pierdes el acceso. Desactivarla es un clic: la suscripción no se renovará y terminará el {{date}}.',
  'billing.autoRenew.notePaused':
    'Cobro el {{date}}, al terminar la pausa, con la tarjeta guardada. Puedes desactivar la renovación cuando quieras: entonces la suscripción no se reanudará tras la pausa.',
  'billing.autoRenew.remove': 'Eliminar la tarjeta',
  'billing.autoRenew.changeCard': 'Cambiar la tarjeta',
  'billing.autoRenew.cancel': 'Cancelar la suscripción',
  'billing.autoRenew.nextCharge': 'Próximo cobro: {{date}} · {{amount}}',
  'billing.autoRenew.nextChargeDate': 'Próximo cobro: {{date}}',
  'billing.autoRenew.canceledUntil': 'Suscripción activa hasta el {{date}}; no se renovará.',
  'billing.autoRenew.canceled': 'Renovación automática desactivada.',
  'billing.pause.running': 'En pausa hasta el {{until}}: sin acceso, cobro el {{until}}',
  'billing.pause.scheduled':
    'Desde el {{from}}, pausa hasta el {{until}}: se cerrará el acceso, cobro el {{until}}',
  'billing.pause.resume': 'Reanudar ahora',
  'billing.pause.resumeNote':
    'Cobraremos la tarifa habitual en un par de horas y el acceso se abrirá al momento.',
  'billing.pause.cancel': 'Cancelar la pausa',
  'billing.autoRenew.declined':
    'No se ha podido cobrar: actualiza la tarjeta para seguir con la renovación automática.',
  'billing.autoRenew.updateCard': 'Actualizar la tarjeta',
  // ── Churn reason ──────────────────────────────────────────
  'churn.title.autoRenew': '¿Desactivar la renovación automática?',
  'churn.title.card': '¿Eliminar la tarjeta?',
  'churn.why': '¿Por qué?',
  'churn.value': 'Este mes tus búsquedas te han traído {{n}} {{word}}',
  'churn.confirm.autoRenew': 'Desactivar la renovación',
  'churn.confirm.card': 'Eliminar la tarjeta',
  'churn.pause': 'Pausa de 1 mes; después sigue al mismo precio',
  'churn.pauseNote': 'El acceso se cerrará el {{from}}, sin cobros hasta el {{to}}.',
  'churn.pauseNoteNoDate': 'El acceso se cerrará durante un mes, sin cobros.',
  'churn.pickReason': 'Elige un motivo',
  'churn.detailsLabel': 'Más detalles (opcional)',
  'churn.detailsRequired': 'Cuéntanos un poco más',
  'churn.retain.noRelevantTenders':
    'Escríbenos a {{brandEmail}} y ajustamos la búsqueda contigo en 10 minutos',
  'churn.retain.poorAlerts': 'Afinamos la búsqueda contigo: escríbenos a {{brandEmail}}',
  'churn.retain.changingCard':
    'No hace falta eliminar la tarjeta: vincula la nueva y sustituirá a la anterior',
  'churn.reason.changingCard': 'Voy a cambiar de tarjeta',
  'churn.reason.noCardStorage': 'No quiero guardar la tarjeta',
  'churn.reason.stopRenewing': 'No voy a renovar',
  'churn.reason.noRelevantTenders': 'No hay licitaciones que me sirvan',
  'churn.reason.didNotWin': 'No he ganado',
  'churn.reason.noTime': 'No tengo tiempo',
  'churn.reason.foundBetter': 'He encontrado otro servicio',
  'churn.reason.tooExpensive': 'Es caro',
  'churn.reason.oneOffNeed': 'Lo necesitaba una sola vez',
  'churn.reason.poorAlerts': 'Pocas alertas útiles',
  'churn.reason.hardToUse': 'Es difícil de usar',
  'churn.reason.paymentIssue': 'Problema con el pago',
  'churn.reason.other': 'Otro',
  'billing.history.title': 'Historial de pagos',
  'billing.history.showAll': 'Ver todos ({{count}})',
  'billing.history.fiscalReceipt': 'Recibo',
  'billing.history.status.completed': 'Pagado',
  'billing.history.status.pending': 'Pendiente de pago',
  'billing.history.status.preparing': 'En proceso',
  'billing.history.status.cancelled': 'Cancelado',
  'billing.autoRenew.addPrompt':
    'Vincula una tarjeta para que la suscripción se renueve sola, sin pagar a mano cada periodo.',
  'billing.autoRenew.addCard': 'Vincular tarjeta',
  'billing.card.title': 'Vincular tarjeta',
  'billing.card.titleSubscribe': 'Contratar la suscripción',
  'billing.card.subtitle': 'Stripe procesa los datos de la tarjeta. Nosotros no la guardamos.',
  'billing.card.subscribeSubtitle': 'No guardamos tu tarjeta: los datos los procesa Stripe.',
  'billing.card.securedBy': 'Pago seguro',
  'billing.card.planLabel': 'Plan',
  'billing.card.chargeNowLabel': 'Se cobra ahora',
  'billing.card.policyPrefix': 'Al suscribirte, aceptas',
  'billing.card.policyOffer': 'las condiciones',
  'billing.card.renewLabel': 'Renovación automática',
  'billing.card.thenMonthly': '{{amount}} al mes',
  'billing.card.thenYearly': '{{amount}} al año',
  'billing.card.upsellYear': 'Pásate al plan anual: {{months}} meses gratis',
  // es (owner, 2026-10-20): the UZ pitch, «contacts inside».
  'billing.card.pitchCategory':
    '{{count}} licitaciones de tu categoría cierran esta semana: contactos y pliegos, dentro',
  'billing.card.pitchMarket': 'Ahora hay {{count}} licitaciones abiertas por {{sum}} €',
  'billing.card.payAmount': 'Pagar {{amount}}',
  'billing.card.nextChargeOn': 'Próximo cobro: {{date}}.',
  'billing.card.remindTelegram': 'Te lo recordaremos por Telegram.',
  'billing.card.remindEmail': 'Te lo recordaremos por email.',
  'billing.card.cancelHow': 'Se desactiva en dos clics en «Planes».',
  'billing.card.cancelAnytime':
    'Puedes cancelar la renovación automática cuando quieras desde tu cuenta.',
  'billing.card.periodMonthly': 'al mes',
  'billing.card.periodAnnual': 'al año',
  'billing.card.otpRecap': 'Pago de {{amount}} con la tarjeta {{card}}',
  'billing.card.receiptPayme': 'El recibo del pago llegará a tu email',
  'billing.card.receiptOcto': 'El recibo estará disponible en el historial de pagos',
  'billing.card.timelineToday': 'Hoy',
  'billing.card.timelineTodayText': '{{amount}}, el acceso se abre al momento',
  'billing.card.timelineRemind': 'email recordatorio de la renovación',
  'billing.card.timelineRemindTelegram': 'te avisamos por Telegram',
  'billing.card.timelineRemindEmail': 'te avisamos por email',
  'billing.card.timelineRenew': 'renovación',
  'billing.card.timelineToggle': 'Pago y renovación',
  'billing.card.renewLine': 'Renovación: {{date}}.',
  'billing.card.nextHint': 'Después, tu banco puede pedirte un código de confirmación.',
  'billing.card.smsFromPayme': 'El código te lo envía tu banco; no vemos el número de tu tarjeta',
  // {{lots}} / {{appears}} agree with the count in code (lib/niche-hint.ts, es
  // branch): «1 lote abierto», «se publica 1 lote nuevo», «se publican 5 lotes nuevos».
  'billing.card.nicheOpen': 'Para tus búsquedas hay ahora {{count}} {{lots}}',
  'billing.card.nicheDirection': 'Para tus búsquedas {{appears}} {{count}} {{lots}} al mes',
  'billing.card.nicheDaily': 'Cada día {{appears}} {{count}} {{lots}}',
  'billing.card.securedByPay': 'Pago seguro: lo procesa Stripe',
  'billing.card.securedByOcto': 'Pago seguro: lo procesa Stripe',
  // Product decision (es): 14-day refund guarantee (EU withdrawal right), not 7.
  'billing.card.refund7': '14 días de garantía de devolución',
  'billing.card.securedByClick': 'Pago seguro: lo procesa Stripe',
  'billing.card.securedByPayClick': 'Pago seguro: lo procesa Stripe',
  'billing.card.supportTg': 'Soporte',
  'billing.card.offerLink': 'Condiciones',
  'billing.card.privacyLink': 'Privacidad',
  'billing.card.policyAnd': 'y',
  'billing.card.policyPrivacy': 'la política de privacidad',
  'billing.card.policySuffix': '.',
  // The button that leaves for Stripe: it must say that pressing it means
  // paying (TRLGDCU art. 98.2), not just «subscribe».
  'plans.subscribeWithCard': 'Suscribirme a {{name}} y pagar',
  'plans.payOnceLead': 'El pago se hace en Stripe · no introduces la tarjeta aquí',
  'plans.payOnceLeadVia': 'El pago se hace en {{gateway}} · no introduces la tarjeta aquí',
  'plans.payOnceBtn': 'Pago único',
  'plans.cardOnlyCta': 'Pagar con tarjeta',
  'plans.checkoutLinkFailed':
    'No se ha podido abrir la página de pago. Inténtalo de nuevo en un momento o escríbenos a {{brandEmail}}.',
  'plans.stripe.renews':
    'Suscripción mensual con renovación automática: se cobra cada mes hasta que la canceles. Puedes cancelarla cuando quieras aquí mismo, en «Tu suscripción», y conservas el acceso hasta el final del periodo pagado.',
  'plans.stripe.alreadySubscribed':
    'Ya tienes una suscripción que se renueva. Para cambiar de plan, desactiva abajo la renovación automática (conservas el periodo pagado) y contrata el nuevo.',
  'plans.stripe.payee': 'Los pagos con tarjeta los recibe {{name}} (EE. UU.) a través de Stripe',
  // The card form in OUR modal (Stripe Elements, 2026-10-21) — the card is typed
  // on milicita.es, in Stripe's fields. Refusals in our words, not Stripe's.
  'billing.stripe.payMonthly': 'Pagar {{amount}} / mes',
  'billing.stripe.loading': 'Preparando el formulario de pago…',
  'billing.stripe.loadFailed':
    'No se ha podido abrir el formulario de pago. Cierra la ventana e inténtalo de nuevo.',
  'billing.stripe.errorFunds':
    'La tarjeta no tiene saldo suficiente. Recárgala o paga con otra tarjeta.',
  'billing.stripe.errorExpired': 'La tarjeta ha caducado. Paga con otra tarjeta.',
  'billing.stripe.errorCard': 'Revisa el número de la tarjeta, la fecha de caducidad y el CVC.',
  'billing.stripe.error3ds':
    'Tu banco no ha confirmado el pago (3-D Secure). Inténtalo de nuevo o paga con otra tarjeta.',
  'billing.stripe.errorDeclined':
    'Tu banco ha rechazado el pago. Prueba con otra tarjeta o consulta con tu banco.',
  // Chile: under a refusal in the card sheet, before the support address.
  'billing.stripe.declineHelp': '¿No pasa el pago? Escríbenos y te ayudamos:',
  'billing.stripe.errorGeneric': 'El pago no se ha completado. Inténtalo de nuevo.',
  // Who the invoice names (`payments.checkoutCollects`): what the Stripe page
  // asked for before the form moved here. Reverse charge needs the buyer's NIF.
  'billing.stripe.priceSuffix': '/ mes',
  'billing.stripe.billingTitle': 'Datos de facturación',
  'billing.stripe.billingHint':
    'Opcional. Si los añades, aparecerán en tus facturas. También puedes añadirlos después, en Ajustes.',
  // Owner, 2026-10-22: the card form is price + card + pay like KazTender's; the
  // invoice details are one collapsed link, and the IVA sentence sits behind «more».
  'billing.stripe.invoiceToggle': 'Datos para la factura (opcional)',
  'billing.stripe.termsHeadline': 'IVA no incluido · 14 días de garantía de devolución',
  'billing.stripe.taxMore': 'Más información sobre el IVA',
  // After the payment: /billing/return and settings (`InvoiceDetailsPrompt`).
  'billing.invoice.ask': 'Para recibir la factura con tu NIF y dirección, añádelos aquí',
  'billing.invoice.hint': 'Aparecerán en las facturas que se emitan a partir de ahora.',
  'billing.invoice.save': 'Guardar datos',
  'billing.invoice.saved':
    'Guardado. Saldrán en tus próximas facturas. Si necesitas que la factura de este pago los lleve, escríbenos a {{brandEmail}}.',
  'billing.invoice.failed': 'No se han podido guardar los datos. Inténtalo de nuevo.',
  'billing.invoice.onFile': 'Datos de facturación guardados: {{details}}',
  'billing.stripe.fieldName': 'Razón social o nombre y apellidos',
  'billing.stripe.fieldTaxId': 'NIF / CIF / NIE',
  'billing.stripe.fieldLine1': 'Dirección',
  'billing.stripe.fieldLine1Placeholder': 'Calle, número, piso',
  'billing.stripe.fieldPostalCode': 'Código postal',
  'billing.stripe.fieldCity': 'Ciudad',
  'billing.stripe.fieldState': 'Provincia',
  'billing.stripe.fieldCountry': 'País',
  'billing.stripe.errorTaxId': 'Este NIF no es válido: revisa la letra o el dígito de control.',
  'billing.stripe.errorPostalCode': 'Escribe un código postal español de 5 cifras.',
  'billing.stripe.errorBilling': 'Revisa los datos de facturación.',
  'plans.vat.excluded': '+ IVA',
  'plans.vat.included': '(IVA incluido)',
  // Owner, 2026-10-20: no IVA is charged (`payments.vatNotCharged`). After every
  // amount, and the reason once under the plans — the same sentence opens the
  // text under Stripe's pay button (`ES_NO_VAT_NOTE`, stripe-catalog.ts).
  'plans.vat.notCharged': '· IVA no incluido',
  'plans.vat.why':
    'Precio sin IVA. MiLicita es un servicio de AI CRM Inc. (EE. UU.), no establecida en la UE: para empresas y autónomos se aplica la inversión del sujeto pasivo (art. 84.Uno.2.º LIVA).',
  'plans.cardOnlyPrice': '{{amount}} / mes · IVA no incluido',
  'plans.cardOnlyRenews': 'Se renueva cada mes. Puedes desactivarlo en tu cuenta cuando quieras.',
  'plans.onceSheetTitle': 'Plan {{name}}: 1 mes',
  'plans.onceSheetPick': 'Elige cómo pagar',
  'plans.oncePayNow': 'Pagar {{amount}}',
  'plans.oncePreparing': 'Preparando el pago…',
  'plans.onceExternalHint': 'El pago se abrirá en el navegador del móvil',
  'plans.handoffStuck': '¿No se ha abierto? Abre el enlace en el navegador del móvil',
  'plans.handoffCopy': 'Copiar el enlace',
  'plans.handoffCopied': 'Copiado',
  'plans.onceSheetClose': 'Cerrar',

  'plans.onceTimelineTitle': 'Pago y acceso',
  // {{word}} is daysWord's «día» / «días» (lib/format.ts, es branch).
  'plans.onceAccessLine':
    'Acceso durante {{days}} {{word}}, hasta el {{date}}. Sin renovación automática.',
  'plans.onceTimelineSub': 'Acceso durante {{days}} {{word}}',
  'plans.onceTimelineToday': 'Hoy',
  'plans.onceTimelineTodayText': '{{amount}}, el acceso se abre al momento',
  'plans.onceTimelineEnd': 'fin del acceso',
  // No supplier count: the Uzbek figure is not ours. The live line shows a real one.
  'plans.social': 'Licitaciones públicas para empresas de toda España',
  'plans.socialLive': 'Más de {{count}} empresas de toda España ya usan {{brand}}',
  // {{word}} is «licitación abierta» / «licitaciones abiertas» (plans.tsx, es branch).
  'plans.openNow': 'Ahora hay {{count}} {{word}}',
  // The market-first Pro card (`features.marketFirstPitch`): analysis, alerts, then «También».
  'plans.group.search': 'Alertas y búsqueda',
  'plans.group.contacts': 'También',
  'plans.group.analysis': 'Quién gana y por cuánto',
  'plans.business.plus': 'Todo lo de Pro, y además:',
  'plans.octo.title': 'Contratar la suscripción',
  'plans.octo.tabOcto': 'Stripe',
  'plans.octo.tabPayme': 'Tarjeta bancaria',
  'plans.octo.continue': 'Continuar en Stripe',
  'plans.octo.redirectNote': 'Los datos de la tarjeta se introducen en la página segura de Stripe',
  'plans.octo.error': 'No se ha podido iniciar el pago. Inténtalo de nuevo.',
  'plans.accessUntil':
    'Acceso al análisis activo tras el pago, hasta el {{date}} · puedes cancelar cuando quieras',
  'plans.cantPay': '¿No consigues pagar?',
  'plans.trust.secureTitle': 'Pago seguro',
  'plans.trust.secureText': 'Stripe procesa la tarjeta: no vemos ni guardamos su número',
  'plans.trust.noSurpriseTitle': 'Sin cobros ocultos',
  'plans.trust.noSurpriseText':
    'Suscripción mensual: te avisamos antes de cada cobro y puedes desactivar la renovación cuando quieras',
  // Product decision (es): 14 days, the EU withdrawal period (brief §1).
  'plans.trust.refundTitle': 'Garantía de devolución',
  'plans.trust.refundText':
    '¿El primer pago no te ha convencido? Escríbenos en un plazo de 14 días y te devolvemos el dinero',
  'plans.trust.receiptTitle': 'Recibo del pago',
  'plans.trust.receiptText': 'Después del pago, recibirás el recibo en tu email',
  'billing.card.smsFrom':
    'El código de confirmación te lo enviará tu banco (no {{brand}}) al número vinculado a la tarjeta.',
  'billing.card.otpUseLast': 'Introduce el último código que has recibido',
  'billing.card.resendIn': 'Podrás reenviar el código dentro de {{time}}',
  'billing.card.resend': 'Reenviar el código',
  'billing.card.newCodeIn': 'Nuevo código dentro de {{time}}',
  'billing.card.errorResend': 'No se ha podido enviar el código. Inténtalo de nuevo.',
  'billing.card.otpSubtitle': 'Introduce el código de confirmación de tu banco.',
  // ── OTP confirmation step ────────────────────────────────────────────────
  'billing.card.otpTitle': 'Confirma el pago',
  'billing.card.otpBack': 'Atrás',
  'billing.card.otpDigit': 'Dígito {{n}}',
  'billing.card.stepCard': 'Tarjeta',
  'billing.card.stepCode': 'Código',
  'billing.card.stepAccess': 'Acceso activado',
  'billing.card.smsSentTo': 'Tu banco ha enviado el código de confirmación al {{phone}}.',
  'billing.card.smsSentGeneric':
    'Tu banco ha enviado el código de confirmación al número vinculado a la tarjeta.',
  'billing.card.otpChargeLine': '{{scheme}} {{card}} · se cobrarán {{amount}}',
  'billing.card.confirmPay': 'Confirmar el pago',
  'billing.card.otpFraud':
    'No compartas el código con nadie: el equipo de {{brand}} nunca te lo pedirá.',
  'billing.card.number': 'Número de tarjeta',
  'billing.card.expire': 'Caducidad',
  'billing.card.expirePlaceholder': 'MM/AA',
  'billing.card.otp': 'Código',
  'billing.card.next': 'Siguiente',
  'billing.card.confirm': 'Confirmar',
  'billing.card.errorCard':
    'No se ha podido vincular la tarjeta. Revisa el número y la fecha de caducidad.',
  'billing.card.errorCardNotAccepted':
    'Revisa el número y la caducidad de la tarjeta. Si todo está bien, inténtalo de nuevo dentro de un minuto o usa otra tarjeta.',
  'billing.card.errorPaymeUnavailable':
    'El servicio de pago no está disponible ahora mismo. Inténtalo más tarde.',
  'billing.card.errorTinRequired':
    'Este plan requiere el NIF de la empresa. Añádelo en los ajustes y repite el pago: el código de confirmación no tiene nada que ver.',
  'billing.card.errorOtp': 'Código incorrecto. Inténtalo de nuevo.',
  'billing.card.errorChargeFailed':
    'El banco ha rechazado el cobro con esta tarjeta. Prueba con otra.',
  'billing.card.errorInsufficientFunds':
    'La tarjeta no tiene saldo suficiente. Recárgala o usa otra.',
  'billing.card.errorCardNotFound': 'No hay ninguna tarjeta con ese número. Revísalo.',
  'billing.card.errorCorporateCard': 'Esta tarjeta de empresa no se acepta. Usa otra tarjeta.',
  'billing.card.errorCardBlocked': 'El banco ha bloqueado la tarjeta. Usa otra.',
  'billing.card.errorCardExpired': 'La tarjeta ha caducado. Usa otra.',
  'billing.card.errorSmsNotEnabled':
    'La tarjeta no tiene activada la confirmación de pagos en línea. Actívala en tu banco o usa otra tarjeta.',
  'billing.card.errorWrongExpiry': 'Fecha de caducidad incorrecta. Revisa el mes y el año.',
  'billing.card.errorWrongNumber': 'Número de tarjeta incorrecto. Revísalo.',
  'billing.card.errorCardNotServed': 'Esta tarjeta no admite pagos en línea. Usa otra.',
  'billing.card.errorLimitExceeded': 'Has superado el límite mensual de la tarjeta. Usa otra.',
  'billing.card.errorCurrency': 'La moneda de la tarjeta no es válida. Prueba con otra tarjeta.',
  'billing.card.errorProcessingUnavailable':
    'El banco no está disponible en este momento. Inténtalo dentro de unos minutos.',
  'billing.card.errorOtpExpired': 'El código ha caducado. Pide uno nuevo.',
  'billing.card.errorOtpAttempts': 'Has agotado los intentos. Pide un código nuevo.',
  'billing.card.errorEmpty': 'Introduce el número de la tarjeta',
  'billing.card.errorExpireEmpty': 'Indica la caducidad',
  'billing.card.errorIncomplete': 'Introduce los 16 dígitos de la tarjeta',
  'billing.card.errorInvalidNumber': 'El número de tarjeta no es correcto',
  'billing.card.errorUnsupported':
    'Esta tarjeta no se admite. Revisa el número: aceptamos Visa y Mastercard.',
  'billing.card.errorPaymeBrandUnsupported':
    'Esta tarjeta no admite pagos recurrentes. Haz un pago único con el botón «Pago único» del plan.',
  'billing.card.errorUnsupportedOcto': 'Esta tarjeta no se admite. Usa una Visa o Mastercard.',
  'billing.card.payViaOcto': 'Pagar con otra tarjeta',
  'billing.card.foreignBrandHint': 'Aceptamos tarjetas Visa y Mastercard.',
  'billing.card.corporateOcto': 'Algunas tarjetas de empresa pueden no aceptarse.',
  'billing.card.errorExpired': 'Ha caducado',
  'billing.card.errorExpireInvalid': 'Fecha no válida',
  'plans.perYear': '/ año',
  'plans.periodMonth': 'mes',
  'plans.periodYear': 'año',
  'plans.perMonth': '/ mes',
  'plans.from': 'desde',
  // {{word}} is plansWord's «plan» / «planes» (lib/format.ts, es branch).
  'plans.tiersCount': '{{count}} {{word}} a elegir',
  'plans.bannerYear': 'Un año por el precio de 6 meses',
  'plans.bannerPair': 'Pro y Business · pago mensual',
  'plans.name.pro': 'Pro',
  'plans.name.business': 'Business',
  'plans.name.ip': 'Individual',
  'plans.name.free': 'Gratis',
  'plans.som': '€',
  'plans.feature.opens': '{{count}} consultas de contacto al mes',
  'plans.feature.categoryLimit': 'Hasta {{count}} categorías',
  'plans.feature.categoryLimitUnlimited': 'Categorías CPV ilimitadas',
  'plans.feature.categoryNiche': '1 sector CPV + alertas por email o en Telegram',
  'plans.feature.contactsNiche': 'Catálogo de licitaciones públicas',
  'plans.feature.allTenders':
    'Todas las licitaciones públicas de España (PLACSP y plataformas autonómicas)',
  // Product decision (es): no private/corporate source is harvested in Spain.
  'plans.feature.privateTenders':
    'Todo el sector público: Estado, comunidades autónomas y entidades locales',
  // Spain harvests no IFI source (`sourceKeys` is PLACSP only), so this Pro-card
  // slot names what Pro does open there: the award behind the outcome wall.
  'plans.feature.ifi': 'Adjudicatario e importe de cada licitación',
  // {{word}} is contactsWord's «contacto» / «contactos» (lib/format.ts, es branch).
  'plans.feature.contactsMetered': '{{count}} {{word}} de compradores al mes',
  'plans.feature.budgetCeiling': 'Licitaciones con presupuesto de hasta {{amount}} millones de €',
  // es (owner, 2026-10-20): the órgano's contact is Pro, as on UZ.
  'plans.feature.contactsAll': 'Contactos de los órganos de contratación: email y teléfono',
  // Not a contact: PLACSP-side, ~1 000 of ~146 000 winners in 12 months have an
  // email (owner's measure, 21.10). Rendered on UZ only (routes/plans.tsx).
  'plans.feature.winnerContacts':
    'Historial de adjudicaciones por empresa: qué ha ganado y por cuánto',
  // es (owner, 2026-10-20): the pliegos are Pro, as on UZ.
  'plans.feature.documents': 'Pliegos y documentación de cada licitación (PCAP, PPT y anexos)',
  'plans.feature.procedure': 'Procedimiento y criterios de adjudicación',
  'plans.feature.aiSummary': 'Resumen IA y requisitos de los pliegos, gratis',
  'plans.feature.aiSummaryFull': 'Resumen IA completo y requisitos de los pliegos',
  'plans.feature.savedSearchOne': 'Hasta 3 búsquedas guardadas + alertas por email o en Telegram',
  'plans.feature.savedSearchAll': 'Búsquedas guardadas y alertas sin límite',
  'plans.feature.supplierProfile':
    'Perfil con tus licitaciones ganadas: alertas sin configurar nada',
  'plans.feature.telegramBot':
    'Bot de Telegram: búsqueda, respuestas con IA, resúmenes y gestión de alertas',
  'plans.feature.export': 'Exportación a Excel',
  'plans.feature.buyerAnalytics':
    'Con quién contrata cada comprador: concentración de compras y volumen anual',
  'plans.feature.analytics': 'Baja e importe de adjudicación en licitaciones parecidas',
  // The Pro card's last line (`features.marketFirstPitch`): still Pro, named last.
  'plans.feature.sourceAlso':
    'Contacto del órgano de contratación, pliegos y anuncio oficial en un clic',
  'plans.feature.orgWatch':
    'Seguimiento de 50 compradores y competidores: alertas de nuevos contratos',
  'plans.feature.prioritySupport': 'Soporte prioritario',
  'plans.business.seats': 'Puestos en el equipo: {{count}} (tú y tus empleados)',
  'plans.business.seatsGeneric': 'Varios puestos para el equipo',
  'plans.business.telegram': 'Cada miembro del equipo tiene su bot de Telegram y sus alertas',
  'baseRates.title': 'Datos de mercado',
  'baseRates.hint':
    'Baja mediana sobre el presupuesto base, número de licitadores y rango del importe de adjudicación en licitaciones cerradas de la misma categoría y tamaño parecido (de 0,3 a 3 veces el presupuesto de este lote). Te ayuda a valorar la competencia y un precio justo.',
  'baseRates.competition': 'Licitadores (mediana)',
  'baseRates.discount': 'Baja mediana',
  'baseRates.discountCaption': 'Cuánto bajaron los adjudicatarios sobre el presupuesto base',
  'baseRates.degenerate':
    'El {{pct}} % de las licitaciones tiene el mismo valor: es una regla de la plataforma, no un dato de mercado',
  'baseRates.priceBand': 'Rango del importe de adjudicación',
  'baseRates.priceMedian': 'Importe de adjudicación (mediana)',
  'baseRates.priceBasisBanded': 'Mediana de licitaciones comparables del mismo tamaño',
  'baseRates.priceBasisSection':
    'Mediana de toda la categoría, sin tener en cuenta el tamaño del lote',
  'baseRates.basisBanded':
    'Según {{n}} licitaciones comparables: importe de adjudicación {{min}} – {{max}}, {{from}} – {{to}}',
  'baseRates.basisBandedNoDates':
    'Según {{n}} licitaciones comparables: importe de adjudicación {{min}} – {{max}}',
  'baseRates.basisSection': 'Según {{n}} licitaciones de la categoría, {{from}} – {{to}}',
  'baseRates.basisSectionNoDates': 'Según {{n}} licitaciones de la categoría',
  'baseRates.teaserBasis': 'Según {{n}} licitaciones comparables en {{period}}',
  'baseRates.teaserBasisNoDates': 'Según {{n}} licitaciones comparables',
  'baseRates.teaserMonths.one': '{{n}} mes',
  'baseRates.teaserMonths.few': '{{n}} meses',
  'baseRates.teaserMonths.many': '{{n}} meses',
  'baseRates.sizeBlind':
    'El lote no indica presupuesto, así que se compara con toda la categoría sin tener en cuenta el tamaño del lote.',
  'baseRates.positionBelow':
    'El presupuesto del lote está por debajo del importe habitual de adjudicación en licitaciones así',
  'baseRates.positionInside':
    'El presupuesto del lote está dentro del rango habitual de importes de adjudicación',
  'baseRates.positionAbove':
    'El presupuesto del lote está por encima del importe habitual de adjudicación en licitaciones así',
  'baseRates.insufficient': 'Todavía no hay datos suficientes de esta categoría',
  'baseRates.locked': 'La analítica está disponible en el plan Pro',
  'baseRates.lockedCta': 'Ver planes',
  // The line under the lot's budget card (`features.freeBidderMedian`, WinDiscountLine).
  'baseRates.winLine.question': '¿Cuánto hay que bajar para ganar?',
  'baseRates.winLine.basis': 'Baja mediana en {{n}} lotes parecidos:',
  // ── Market dashboard ───────────────────────────────────────────
  'market.title': 'El mercado de la contratación pública en España',
  'market.subtitle':
    'Licitaciones abiertas y adjudicaciones en cifras: presupuestos, comunidades autónomas, sectores y evolución.',
  'market.summary':
    'Ahora hay {{active}} licitaciones públicas abiertas en España por un total de {{budget}}; en la última semana se han añadido {{newWeek}}. Abajo tienes el presupuesto por comunidad autónoma y sector, y las adjudicaciones de las licitaciones cerradas.',
  'market.seo.title': 'Contratación pública en España: estadísticas y análisis',
  'market.seo.description':
    'Análisis del mercado de contratación pública en España: cuántas licitaciones hay abiertas, presupuestos por comunidad autónoma y sector, volumen y competencia según las adjudicaciones.',
  'market.som': '€',
  'market.count': '{{count}} contratos',
  'market.generatedAt': 'Datos a {{date}}',
  'market.kpi.active': 'Licitaciones abiertas',
  'market.kpi.budget': 'Presupuesto en el mercado',
  'market.kpi.newWeek': 'Nuevas esta semana',
  'market.kpi.closingSoon': 'Cierran pronto',
  'market.kpi.awardedCount': 'Adjudicaciones en el año',
  'market.kpi.awardedVolume': 'Volumen en el año',
  'market.direct':
    'Adjudicaciones sin concurrencia (contratos menores y negociados sin publicidad) en el mismo periodo: {{count}} por {{volume}}. No se incluyen en las cifras de arriba.',
  'market.byRegion': 'Presupuesto por comunidad autónoma',
  'market.byCategory': 'Presupuesto por categoría',
  'market.newPerWeek': 'Nuevas licitaciones por semana',
  'market.awardedByYear': 'Adjudicaciones por año',
  'chart.noData': 'sin datos',
  'market.competition': 'Licitadores (mediana) por sector',
  'market.discount': 'Baja mediana por sector',
  'market.range.from': 'Desde',
  'market.range.to': 'Hasta',
  'market.range.reset': 'Restablecer el periodo',
  'nav.market': 'Mercado',
  'prices.title': 'Precios de referencia',
  'prices.subtitle': 'Cuánto paga de media la Administración por cada categoría de producto',
  'prices.seoTitle': '{{name}}: precios en la contratación pública',
  'prices.seoDesc.bandDated':
    'Importe mediano del contrato «{{name}}»: {{median}} €, rango {{p25}}–{{p75}}. Según {{n}} contratos públicos adjudicados en España entre {{from}} y {{to}}.',
  'prices.seoDesc.band':
    'Importe mediano del contrato «{{name}}»: {{median}} €, rango {{p25}}–{{p75}}. Según {{n}} contratos públicos adjudicados en España.',
  'prices.seoDesc.sample':
    'Estadísticas de contratos públicos adjudicados de «{{name}}» en España: muestra de {{n}} contratos con importe de adjudicación.',
  'prices.seoDesc.empty':
    '¿Cuánto paga la Administración por «{{name}}»? Datos de contratos públicos adjudicados en España.',
  'prices.search': 'Buscar producto o código',
  'prices.count': '{{count}} contratos',
  'prices.empty': 'Todavía no hay categorías con suficiente historial de contratos.',
  'prices.back': '← A los productos',
  'prices.locked': 'El análisis de precios está disponible en Pro.',
  'prices.locked.cta': 'Hazte Pro',
  'prices.insufficient':
    'Todavía hay pocos contratos adjudicados en esta categoría para dar estadísticas.',
  'prices.parentFallback':
    'Aún no hay contratos adjudicados con el código {{code}}: mostramos los precios de la categoría más amplia «{{name}}».',
  'prices.band': 'Importe de adjudicación',
  'prices.band.p25': '25 %: por debajo',
  'prices.band.median': 'mediana',
  'prices.band.p75': '75 %: por encima',
  'prices.discount': 'Baja mediana sobre el presupuesto base',
  'prices.basedOn':
    'según {{n}} contratos de la muestra en los que la fuente publica el presupuesto base',
  'prices.discountUnknown':
    'sin datos: la fuente registra el presupuesto base como importe final ({{n}} contratos)',
  'prices.noSourceData': 'la fuente no publica estos datos',
  'prices.sampleNoDates': 'Muestra: {{n}} contratos · la fuente no publica fechas',
  'prices.sortBy': 'Ordenar:',
  'prices.sortCount': 'Por volumen de datos',
  'prices.sortName': 'Por nombre',
  'prices.participants': 'Licitadores (mediana)',
  'prices.sample': 'Muestra: {{n}} contratos · {{from}}–{{to}}',
  'prices.direct':
    'Adjudicaciones sin concurrencia: {{n}}, mediana {{median}}. No entran en el rango de precios.',
  'prices.caveat': 'Se muestra el importe del contrato por categoría, no el precio por unidad.',
  'nav.prices': 'Precios',
  'nav.results': 'Adjudicaciones',
  // «Próximos vencimientos» — Spain only (features.expiringContracts).
  'nav.expiring': 'Próximos vencimientos',
  'expiring.title': 'Próximos vencimientos de contratos públicos',
  'expiring.intro':
    'Contratos adjudicados cuyo plazo termina pronto. Cuando un contrato vence, el órgano suele volver a licitarlo: así puedes preparar tu oferta antes de que se publique.',
  'expiring.note':
    'Fecha estimada con la adjudicación (o la formalización) y la duración que publica la Plataforma de Contratación del Sector Público. Es probable, no garantizado: el contrato puede prorrogarse o licitarse de nuevo antes. No incluye contratos menores ni contratos de menos de 6 meses.',
  'expiring.seoTitle': 'Próximos vencimientos de contratos públicos en España',
  'expiring.seoDesc':
    'Contratos públicos que vencen en los próximos 3, 6 y 12 meses: órgano de contratación, objeto, CPV y comunidad autónoma. Adelántate a la próxima licitación.',
  'expiring.windowAria': 'Plazo de vencimiento',
  'expiring.window.3m': '3 meses',
  'expiring.window.6m': '6 meses',
  'expiring.window.12m': '12 meses',
  'expiring.filter.search': 'Buscar por objeto u órgano',
  'expiring.filter.allRegions': 'Todas las comunidades',
  'expiring.filter.allCpv': 'Todos los sectores (CPV)',
  'expiring.filter.buyer': 'Órgano: {{name}}',
  'expiring.filter.clearBuyer': 'Quitar el filtro de órgano',
  'expiring.count': 'Contratos que vencen en este plazo: {{count}}',
  'expiring.empty': 'No hay contratos que venzan en este plazo con estos filtros.',
  'expiring.emptyReset': 'Quitar filtros',
  'expiring.noSubject': 'Contrato sin descripción',
  'expiring.filterByBuyer': 'Solo este órgano',
  'expiring.term': 'Del {{start}} al {{end}}',
  'expiring.winner': 'Adjudicatario actual',
  'expiring.locked': 'Adjudicatario e importe, en Pro',
  'expiring.endsOn': 'Vence el {{date}}',
  'expiring.today': 'Vence hoy',
  'expiring.inOneDay': 'Vence mañana',
  'expiring.inDays': 'Faltan {{count}} días',
  'expiring.extendable': 'Prorrogable',
  'baseRates.pricesLink': 'Historial completo de precios de la categoría →',
  'similarWinners.title': 'Posibles licitadores',
  'similarWinners.subtitle':
    'Probablemente se presentarán: han ganado contratos así en el último año',
  'similarWinners.hint':
    'Empresas que han obtenido contratos con el mismo código CPV en los últimos 12 meses, por licitación o de forma directa. Si con el código exacto hay menos de tres empresas, se usa la clase del código, más amplia.',
  'similarWinners.teaserBasis': '{{awards}} · {{winners}} en el último año',
  'similarWinners.teaserCompanyN': 'Empresa n.º {{n}}',
  'similarWinners.share': 'Cuota de contratos',
  'similarWinners.basisExact': 'Código CPV {{code}}: {{awards}}, {{winners}} en 12 meses',
  'similarWinners.basisClass':
    'Clase CPV {{code}} (con el código exacto hay pocas empresas): {{awards}}, {{winners}} en 12 meses',
  'similarWinners.wins.one': '{{n}} contrato',
  'similarWinners.wins.few': '{{n}} contratos',
  'similarWinners.wins.many': '{{n}} contratos',
  'similarWinners.companies.one': '{{n}} empresa',
  'similarWinners.companies.few': '{{n}} empresas',
  'similarWinners.companies.many': '{{n}} empresas',
  'companyList.showAll': 'Ver todas ({{n}})',
  'companyList.last': 'última',
  'companyList.search': 'Buscar empresa',
  'companyList.notFound': 'No se ha encontrado nada',
  'companyList.close': 'Cerrar',
  'companyList.share': 'cuota {{pct}} %',
  'companyList.loading': 'Cargando la lista…',
  'companyList.error': 'No se ha podido cargar la lista',
  'companyList.truncated': 'Se muestran las {{n}} primeras de {{total}}',
  'companyList.approxDate':
    'Fecha límite de presentación: la plataforma no publica la fecha de adjudicación',
  'companyList.kind.tender': 'licitación',
  'companyList.kind.direct': 'directa',
  'companyList.kind.mixed': 'licitación y directa',
  'supplierGraph.title': 'Concentración de adjudicatarios del comprador',
  'supplierGraph.hint':
    'Hasta qué punto las compras de este comprador se concentran en pocas empresas y si es fácil entrar como nuevo licitador. Cuantos menos contratos, más prudente es la conclusión: por debajo de 10 es provisional y por debajo de 5 no la damos, solo las cifras. No se cuentan las adjudicaciones directas sin licitación.',
  'supplierGraph.teaserSuppliers': 'Adjudicatarios y sus cuotas',
  'supplierGraph.teaserConcentration': 'Concentración',
  'supplierGraph.teaserSupplierN': 'Empresa n.º {{n}}',
  'supplierGraph.teaserBasis': '{{contracts}} · {{suppliers}} en el historial del comprador',
  'supplierProfile.title': 'Perfil de la empresa',
  // Chile: a natural person's page (`companyIdForm.naturalPerson`).
  'supplierProfile.titlePerson': 'Perfil del proveedor',
  'supplierProfile.back': '← Atrás',
  'supplierProfile.locked': 'Los perfiles de empresas están disponibles en Pro.',
  'supplierProfile.lockedCta': 'Hazte Pro',
  'supplierProfile.empty': 'Todavía no hay datos de esta empresa.',
  'supplierProfile.contracts': 'Contratos',
  'supplierProfile.totalValue': 'Importe de los contratos',
  'supplierProfile.avgValue': 'Contrato medio',
  'supplierProfile.buyers': 'Compradores',
  'supplierProfile.tenure': 'En el mercado',
  'supplierProfile.tenureUnknown': 'la fuente no publica las fechas de los contratos',
  'supplierProfile.p12.title': 'Contratos públicos en 12 meses',
  'supplierProfile.p12.wins': 'Contratos',
  'supplierProfile.p12.value': 'Importe de los contratos',
  'supplierProfile.p12.discount': 'Baja mediana sobre el presupuesto base',
  'supplierProfile.p12.discountNote': 'según {{n}} contratos con presupuesto base',
  'supplierProfile.p12.participants': 'Licitadores (mediana) en los lotes ganados',
  'supplierProfile.p12.bids': 'Participación detallada',
  'supplierProfile.p12.bidsNote':
    '{{wins}} adjudicaciones de {{n}} lotes en los que la fuente publica todos los licitadores; no se conoce la tasa total de participación',
  'supplierProfile.p12.source':
    'Según las adjudicaciones de contratos públicos en la base de {{brand}}, con fecha de adjudicación en los últimos 12 meses.',
  'supplierProfile.topBuyers': 'Principales compradores',
  'supplierProfile.categories': 'Categorías',
  'supplierProfile.recentContracts': 'Contratos recientes',
  'supplierProfile.allContracts': 'Contratos',
  'supplierProfile.contractsMore': 'Ver más',
  'supplierProfile.regions': 'Comunidades autónomas',
  'supplierProfile.debarred': 'Con prohibición de contratar',
  'supplierProfile.nContracts': '{{n}} contr.',
  'buyer.title': 'Perfil del comprador',
  'buyer.back': '← Atrás',
  'buyer.locked':
    'Las empresas adjudicatarias de este comprador, sus cuotas y los adjudicatarios recurrentes, en Pro.',
  'buyer.lockedCta': 'Hazte Pro',
  'buyer.tin': 'NIF',
  'org.tinNumber': 'NIF {{number}}',
  'buyer.seoTitle': '«{{name}}», NIF {{tin}}: contratos y adjudicatarios',
  'buyer.seoDescription':
    '{{name}}, NIF {{tin}}: {{awards}} contratos por {{value}}, {{suppliers}} empresas adjudicatarias, {{active}} licitaciones abiertas. Categorías e importes en {{brand}}.',
  'supplier.seoTitle': '«{{name}}», NIF {{tin}}: contratos públicos y adjudicaciones',
  'supplier.seoDescription':
    '{{name}}, NIF {{tin}}: {{contracts}} contratos públicos por {{value}}, {{buyers}} compradores, {{period}}. Categorías y comunidades autónomas en {{brand}}.',
  'buyer.empty': 'Todavía no hay datos de este comprador.',
  'buyer.dependenceFlag': 'Dependencia de una sola empresa: {{pct}} % del gasto',
  'buyer.kpi.awards': 'Contratos',
  'buyer.kpi.value': 'Importe de los contratos',
  'buyer.kpi.suppliers': 'Empresas adjudicatarias',
  'buyer.kpi.active': 'Licitaciones abiertas',
  'buyer.p12.title': 'Compras en 12 meses',
  'buyer.p12.awards': 'Contratos firmados',
  'buyer.p12.value': 'Importe de los contratos',
  'buyer.p12.discount': 'Baja mediana sobre el presupuesto base',
  'buyer.p12.discountNote': 'según {{n}} lotes con presupuesto base',
  'buyer.p12.participants': 'Licitadores (mediana)',
  'buyer.p12.participantsNote': 'según {{n}} lotes con número de licitadores',
  'buyer.p12.singleBidder': 'Lotes con un solo licitador',
  'buyer.p12.source':
    'Según las adjudicaciones de contratos públicos en la base de {{brand}}, con fecha de adjudicación en los últimos 12 meses.',
  'buyer.settlement.below': 'Contrato por debajo de la oferta',
  'buyer.settlement.above': 'Contrato por encima de la oferta',
  'buyer.settlement.sub': 'según {{n}} contratos · {{pct}} % por debajo',
  'buyer.settlement.source': 'Fuente: datos abiertos de contratos',
  'buyer.favoritism': 'Concentración y favoritismo',
  'buyer.topShareSplit':
    'Principal adjudicatario: {{value}} % del importe · {{count}} % de los contratos',
  'buyer.streak': '{{name}} ganó {{n}} veces seguidas',
  'buyer.winnerSub':
    '{{wins}} adjudicaciones · {{value}} % del importe / {{count}} % de los contratos',
  'buyer.awardsByYear': 'Contratos por año',
  'buyer.categories': 'Categorías',
  'buyer.recentAwards': 'Contratos recientes',
  // A contract whose published amount is under 1 € (a placeholder the buyer typed) — `amountShown`.
  'buyer.amountUndisclosed': 'Sin importe publicado',
  'buyer.recentTenders': 'Licitaciones recientes',
  'buyer.viewProfile': 'Perfil del comprador',
  'supplierGraph.topShare': 'Cuota del principal adjudicatario',
  'supplierGraph.topShareBasis': '{{wins}} de {{total}} contratos',
  'supplierGraph.locked': 'La analítica está disponible en Pro',
  'supplierGraph.lockedCta': 'Ver planes',
  'supplierGraph.insufficient': 'Todavía no hay datos suficientes de este comprador',
  'supplierGraph.concentration.high': 'Las compras se las llevan 1 o 2 empresas',
  'supplierGraph.concentration.moderate': 'Hay adjudicatarios habituales',
  'supplierGraph.concentration.low': 'Los adjudicatarios cambian',
  'supplierGraph.concentrationHint.high': 'Es difícil entrar como nuevo licitador',
  'supplierGraph.concentrationHint.moderate': 'Se puede entrar, pero hay competidores habituales',
  'supplierGraph.concentrationHint.low': 'Es fácil entrar como nuevo licitador',
  'supplierGraph.tooEarly': '{{contracts}}: es pronto para hablar de concentración',
  'supplierGraph.concentrationHedged.high':
    'Las compras se las llevan 1 o 2 empresas · {{contracts}}',
  'supplierGraph.concentrationHedged.moderate': 'Hay adjudicatarios habituales · {{contracts}}',
  'supplierGraph.concentrationHedged.low': 'Los adjudicatarios cambian · {{contracts}}',
  'supplierGraph.hhiTitle': 'Índice de concentración HHI {{n}}',
  'supplierGraph.dependenceFlag': 'Alta dependencia de una sola empresa',
  'supplierGraph.contracts.one': '{{n}} contrato',
  'supplierGraph.contracts.few': '{{n}} contratos',
  'supplierGraph.contracts.many': '{{n}} contratos',
  'supplierGraph.suppliers.one': '{{n}} empresa',
  'supplierGraph.suppliers.few': '{{n}} empresas',
  'supplierGraph.suppliers.many': '{{n}} empresas',
  'supplierGraph.top3Share': 'Cuota de los 3 principales',
  'supplierGraph.suppliersBasis': 'de {{n}} empresas',
  'supplierGraph.marketShare': 'Cuota del gasto del comprador',
  'supplierGraph.topNShare': 'top {{n}} · {{pct}} %',
  'supplierGraph.footer': '{{suppliers}} empresas · {{contracts}} contratos · {{total}} €',
  // Company registry trust strip. No such registry is ingested for Spain yet;
  // the wording stays generic so nothing names an Uzbek or Kazakh authority.
  'tender.trust.title': 'Sobre el comprador',
  'tender.trust.titleSupplier': 'Sobre la empresa',
  'tender.trust.vat': 'Sujeto pasivo de IVA',
  'tender.trust.fund': 'Capital social',
  'tender.trust.since': 'En actividad desde {{year}}',
  'tender.trust.statusLabel': 'Estado',
  'tender.trust.vatLabel': 'IVA',
  'tender.trust.vatYes': 'Sujeto pasivo',
  'tender.trust.sinceLabel': 'En actividad desde',
  'tender.trust.status.ACTIVE': 'Activa, con obligaciones fiscales',
  'tender.trust.status.TAX_FREE': 'Sin obligaciones fiscales',
  'tender.trust.status.INACTIVE': 'En liquidación',
  'tender.trust.status.LIQUIDATED': 'Ha cesado su actividad',
  'tender.trust.liquidated': 'Liquidada',
  'tender.trust.kgd.DEBTOR_BANKRUPT': 'Declarada en concurso por resolución judicial',
  'tender.trust.kgd.DEBTOR_REHABILITATION': 'En reestructuración por resolución judicial',
  'tender.trust.kgd.DEBTOR_LIQUIDATION_NO_BANKRUPTCY':
    'Liquidación sin concurso por resolución judicial',
  'tender.trust.kgd.BANKRUPT': 'En la lista oficial de empresas en concurso',
  'tender.trust.kgd.PSEUDO_ENTERPRISE': 'Declarada empresa ficticia',
  'tender.trust.kgd.REGISTRATION_INVALID': 'Inscripción declarada nula',
  'tender.trust.kgd.REREGISTRATION_INVALID': 'Reinscripción declarada nula',
  'tender.trust.kgdSource': 'Según datos oficiales de la administración tributaria',
  'tender.trust.dxmapRatingLabel': 'Índice de solidez',
  'tender.trust.dxmapSectorLabel': 'Actividad',
  'tender.trust.dxmapSource': 'índice de solidez de la administración tributaria, según datos de',
  'requirements.title': 'Requisitos de la licitación',
  'requirements.scope': 'Objeto del contrato',
  'requirements.qualifications': 'Requisitos de solvencia',
  'requirements.technicalSpecs': 'Especificaciones técnicas',
  'requirements.delivery': 'Plazos de entrega',
  'requirements.warranty': 'Garantía',
  'requirements.payment': 'Condiciones de pago',
  'requirements.sourceDocuments': 'según los pliegos del lote',
  'requirements.sourceMarketplace': 'según los datos de la plataforma {{name}}',
  'requirements.sourceMarketplaceGeneric': 'según los datos de la plataforma de origen',
  'requirements.disqualifying': 'motivo de exclusión',
  // The scored award criteria (PLACSP `AwardingCriteria`), apart from the solvency list.
  'requirements.awardCriteria': 'Criterios de adjudicación',
  'requirements.awardPoints.one': '{{n}} punto',
  'requirements.awardPoints.few': '{{n}} puntos',
  'requirements.awardPoints.many': '{{n}} puntos',
  'requirements.awardMethod.formula': 'mediante fórmula',
  'requirements.awardMethod.judgement': 'juicio de valor',
  'requirements.noneStated':
    'Hemos analizado los documentos del lote: no indican requisitos de solvencia.',
  'requirements.noDocuments':
    'La plataforma {{name}} no publica aquí los documentos: consúltalos en el origen.',
  'requirements.noDocumentsGeneric':
    'Los documentos de este lote solo se publican en la plataforma de origen.',
  // es (owner, 2026-10-20): the link to the lot on its platform is Pro, as on UZ.
  'requirements.noDocumentsLocked':
    'Los documentos de este lote están en la plataforma de origen. El enlace al lote, en Pro.',
  'requirements.openOnSource': 'Abrir el lote en la plataforma',
  'requirements.unreadable':
    'El documento está escaneado o en un formato que todavía no sabemos leer.',
  'requirements.openDocument': 'Abrir el documento',
  'requirements.pending': 'Estamos analizando los documentos; suele tardar hasta 2 horas.',
  'requirements.locked': 'El análisis de requisitos está disponible en Pro',
  'requirements.lockedCta': 'Ver planes',
  'plans.checkout': 'Contratar',
  'plans.checkoutGuest': 'Entra para contratar',
  'plans.redirecting': 'Redirigiendo…',
  'plans.tinRequired': 'Este plan requiere el NIF de la empresa. Añádelo y vuelve al pago.',
  'plans.checkoutError': 'No se ha podido completar. Inténtalo de nuevo.',
  'plans.loadError': 'No se han podido cargar los planes.',
  'plans.loadRetry': 'Reintentar',
  'plans.payWithPayme': 'Pagar con tarjeta',
  'plans.payOnce': 'Pago único',
  'plans.payOnceWithPrice': 'Pago único: {{amount}}',
  'plans.payOnceNote': 'Sin renovación automática · un 10 % más caro que la suscripción',
  'plans.payOnceNoRenew': 'Pago único sin renovación automática',
  'plans.onceAmount': 'A pagar: {{amount}} por 1 mes',
  'plans.payWithClick': 'Pagar con tarjeta',
  'plans.payOnceViaPayme': 'Pagar con tarjeta',
  'plans.payOnceOrLabel': 'O paga una sola vez: {{amount}}',
  'plans.payMethodLabel': 'Forma de pago',
  'plans.clickCards': 'Visa · Mastercard',
  'plans.paymentVia': 'Pago con tarjeta',
  'plans.payByAccount': 'Pago por transferencia bancaria',
  'plans.invoiceSub': 'Condiciones + factura para tu contabilidad',
  'plans.orPayByCard': 'O paga con tarjeta',
  'plans.yearAccessNote': 'Sin renovación automática: acceso durante 12 meses',
  'plans.payWithCard': 'Pagar con tarjeta (Visa/Mastercard)',
  'plans.payByInvoice': 'Pago con factura (para empresas)',
  'plans.paymeOnlyNote': 'Pago mensual: solo en línea, con tarjeta bancaria.',
  'plans.bankTransferTitle': 'Datos para el pago',
  'plans.bankAmount': 'Importe a pagar',
  'plans.downloadInvoice': 'Descargar la factura proforma',
  'plans.bankNote': 'El plan Pro se activa cuando recibimos la transferencia.',

  // ── Pay by invoice (bankInvoice is off in Spain; kept neutral) ─────────────
  'invoiceFlow.tinLabel': 'NIF de tu organización',
  'invoiceFlow.tinLooking': 'Buscando en el registro…',
  'invoiceFlow.nameLabel': 'Razón social',
  'invoiceFlow.namePlaceholder': 'Ejemplo, S.L.',
  'invoiceFlow.tinIncomplete': 'El {{tin}} tiene {{tinDigits}} caracteres',
  'invoiceFlow.nameRequired': 'Escribe la razón social: aparece en la factura',
  'invoiceFlow.accessPeriod':
    'Acceso de 12 meses desde la activación: si pagas hoy, hasta el {{date}}.',
  'invoiceFlow.issue': 'Emitir factura',
  'invoiceFlow.issuing': 'Emitiendo…',
  'invoiceFlow.errIssue': 'No se ha podido emitir la factura. Revisa el NIF e inténtalo de nuevo.',
  'invoiceFlow.readyTitle': 'Factura n.º {{number}}',
  'invoiceFlow.purposeLabel': 'Concepto de la transferencia (obligatorio)',
  'invoiceFlow.purposeWarning':
    'Cada factura tiene su propio concepto. Si usas el de otra factura, el pago se retrasará. No se aceptan pagos de terceros.',
  'invoiceFlow.copy': 'Copiar',
  'invoiceFlow.copied': 'Copiado',
  'invoiceFlow.download': 'Descargar PDF',
  'invoiceFlow.sendEmail': 'Enviar por email',
  'invoiceFlow.emailSent': 'Enviada por email',
  'invoiceFlow.sendTelegram': 'Enviar a Telegram',
  'invoiceFlow.telegramSent': 'Enviada a Telegram',
  'invoiceFlow.errSend': 'No se ha podido enviar. Descarga el PDF o inténtalo más tarde.',
  'invoiceFlow.errDownload':
    'No se ha podido descargar el PDF: envía la factura por email o a Telegram.',
  'invoiceFlow.errNoEmail': 'Tu cuenta no tiene email: descarga el PDF o envíalo a Telegram.',
  'invoiceFlow.errNoTelegram': 'Telegram no está conectado: conéctalo en los ajustes de alertas.',
  'invoiceFlow.connectTelegram':
    'Conecta Telegram en tu perfil para recibir la factura y las alertas',
  'invoiceFlow.activationNote':
    'El acceso se abrirá en un plazo de 3 días hábiles desde que recibamos el pago.',
  'invoiceFlow.needFaster': '¿Lo necesitas antes?',
  'invoiceFlow.savedNote':
    'La factura queda guardada en tu cuenta: puedes descargarla y volver a enviarla.',
  'plans.freeStart': 'Empezar gratis',
  'plans.currentPlan': 'Plan actual',
  'plans.otherMethods': 'Otras formas de pago',
  'plans.toggleMonthly': 'Mensual',
  'plans.toggleYearly': 'Anual',
  'plans.periodToggleLabel': 'Periodo de pago',
  'plans.badgePopular': 'El más elegido',
  'plans.badgeBestValue': 'Mejor precio',
  'plans.savings': 'Ahorras ~{{percent}} % frente al pago mensual',
  'plans.badgeX2': '×{{multiple}} de ahorro',
  'plans.savingsX2': 'Un año por el precio de 6 meses: ahorras {{som}} €',
  'plans.refYearAria': 'Pagando mes a mes, el año saldría por {{som}} €',
  'plans.consentPrefix': 'Al pagar, aceptas',
  'plans.consentOffer': 'las condiciones',
  'plans.consentAnd': 'y',
  'plans.consentPrivacy': 'la política de privacidad',
  'plans.consentSuffix': '.',
  // ── Footer ─────────────────────────────────────────────────────
  'footer.plans': 'Planes',
  // ── Directory (/directory) ──────────────────────────────
  'directory.title': 'Todas las categorías y comunidades autónomas',
  'directory.intro':
    'Índice completo de las secciones del catálogo: licitaciones públicas de España por sector y por comunidad autónoma del comprador. Junto a cada sección, el número de licitaciones abiertas.',
  'directory.seoTitle': 'Licitaciones por categoría y comunidad autónoma en España',
  // The Uzbek counts («31 направление, 14 регионов») are not Spain's; no numbers.
  'directory.seoDesc':
    'Índice de todas las secciones del catálogo de contratación pública de España: licitaciones por sector y por comunidad autónoma, con el número de licitaciones abiertas en cada una.',
  'directory.categories.h2': 'Por sector',
  'directory.categories.intro':
    'El sector se determina por el grupo de productos del lote, desde materiales de construcción hasta informática y sanidad.',
  'directory.regions.h2': 'Por comunidad autónoma del comprador',
  'directory.regions.intro':
    'La comunidad autónoma es donde está el comprador, no el lugar de entrega.',
  'directory.companies.h2': 'Empresas en la contratación pública',
  'directory.companies.intro':
    'Catálogo de empresas licitadoras y compradores con historial en la contratación pública de España: estado en el registro, fiabilidad y volumen de contratos.',
  'goodStanding.tier.debarred': 'Con prohibición de contratar',
  'goodStanding.tier.liquidated': 'Liquidada',
  'goodStanding.tier.flagged': 'Con alertas fiscales',
  'goodStanding.tier.active_supplier': 'Adjudicataria activa',
  'goodStanding.tierBuyer.active_supplier': 'Comprador activo',
  'goodStanding.tier.active': 'Empresa activa',
  'companies.title': 'Empresas en la contratación pública de España',
  'companies.intro':
    'Empresas licitadoras y compradores con historial en la contratación pública. Junto a cada una, su estado en el registro y el volumen de contratos.',
  'companies.seoTitle': 'Catálogo de empresas de la contratación pública en España',
  'companies.seoTitlePaged': 'Empresas de la contratación pública, página {{page}}',
  'companies.seoDescPaged':
    'Empresas de la contratación pública en España, página {{page}} de {{totalPages}}, {{count}} en total. Compradores y adjudicatarios, contratos e importes en {{brand}}.',
  'companies.seoDesc':
    'Empresas adjudicatarias y compradores de la contratación pública en España: estado en el registro, fiabilidad e historial de contratos de cada una.',
  'companies.search': 'Nombre o NIF',
  'companies.filter.allSectors': 'Todos los sectores',
  'companies.filter.allRegions': 'Todas las comunidades',
  'companies.filter.allRoles': 'Todas las empresas',
  'companies.role.supplier': 'Adjudicatarias',
  'companies.role.buyer': 'Compradores',
  'companies.role.both': 'Compradora y adjudicataria',
  'companies.rowRole.supplier': 'Adjudicataria',
  'companies.rowRole.buyer': 'Comprador',
  'companies.rowRole.both': 'Compradora y adjudicataria',
  'companies.contractsAs.supplier': 'Contratos como adjudicataria: {{count}}',
  'companies.contractsAs.buyer': 'Contratos como compradora: {{count}}',
  'companies.contracts': '{{count}} contratos',
  'companies.empty': 'No se han encontrado empresas. Cambia los filtros.',
  'companies.loadMore': 'Ver más',
  'companies.count': 'Empresas encontradas: {{count}}',
  'nav.companies': 'Empresas',
  'nav.guide': 'Cómo participar',
  'footer.directory': 'Categorías y comunidades',
  'footer.about': 'Quiénes somos',
  'footer.guide': 'Guía para empresas',
  'footer.offer': 'Condiciones',
  'footer.privacy': 'Privacidad',
  'footer.requisites': 'Aviso legal',
  'footer.support': 'Soporte',
  // Cookie consent (`features.cookieConsent`): the footer link and the banner.
  'footer.cookieSettings': 'Configuración de cookies',
  'cookieBanner.title': 'Cookies',
  // First layer, one sentence (owner, 23.10): who, which cookies, own/third-party, that
  // the advertising one profiles, and — through the links beside it — the policy and the
  // second layer. The accept/reject/configure choices are the buttons and the link.
  'cookieBanner.text':
    '{{brand}} usa cookies técnicas y, solo si las aceptas, analíticas y publicitarias (de Meta, según tu perfil de navegación).',
  'cookieBanner.policy': 'Política de cookies',
  'cookieBanner.accept': 'Aceptar todas',
  'cookieBanner.reject': 'Rechazar todas',
  // First layer (owner, 22.10): two equal one-word buttons; the second layer keeps «… todas».
  'cookieBanner.acceptShort': 'Aceptar',
  'cookieBanner.rejectShort': 'Rechazar',
  'cookieBanner.configure': 'Configurar',
  'cookieBanner.settingsTitle': 'Configuración de cookies',
  'cookieBanner.settingsIntro':
    'Elige qué cookies aceptas. Puedes cambiar tu elección cuando quieras desde el enlace «Configuración de cookies» que hay al pie de cada página.',
  'cookieBanner.always': 'Siempre activas',
  'cookieBanner.save': 'Guardar selección',
  'cookieBanner.technical.name': 'Técnicas',
  'cookieBanner.technical.desc':
    'Necesarias para que la web funcione: inicio de sesión, seguridad y recordar esta elección. No se pueden desactivar.',
  'cookieBanner.analytics.name': 'Analíticas',
  'cookieBanner.analytics.desc':
    'Nos permiten medir cómo se usa {{brand}} para mejorarla (PostHog y nuestra propia medición de visitas). Si no las aceptas, solo contamos las páginas vistas y unas pocas acciones (ver una licitación, ver los planes) de forma anónima: sin cookies, sin identificador y sin grabar tu sesión.',
  'cookieBanner.marketing.name': 'Publicitarias',
  'cookieBanner.marketing.desc':
    'Permiten a Meta (Facebook e Instagram) medir nuestras campañas y mostrarte anuncios según tu perfil de navegación (Píxel de Meta y API de conversiones).',
  'nav.about': 'Quiénes somos',
  'nav.subscription': 'Suscripción',
  // ── About page (/about) ────────────────────────────────────────
  'about.pageTitle': 'Sobre {{brand}}',
  'about.subtitle': 'Agregador de licitaciones y contratos públicos de España.',
  'about.lead':
    '{{brand}} es el catálogo único de la contratación pública en España. Reunimos en un solo lugar las licitaciones de las plataformas oficiales, añadimos un análisis de cada una en español y ayudamos a las empresas a encontrar antes los contratos públicos que les encajan.',
  'about.what.title': 'Qué hacemos',
  'about.what.body':
    'Cada pocas horas actualizamos el catálogo de licitaciones públicas, identificamos la comunidad autónoma, la categoría y el objeto de cada una, y las hacemos fáciles de buscar: así no tienes que vigilar a mano decenas de plataformas.',
  'about.feature.catalog.title': 'Catálogo único',
  'about.feature.catalog.desc':
    'Miles de licitaciones vigentes con filtros por comunidad autónoma, categoría, presupuesto y plazo de presentación.',
  'about.feature.ai.title': 'Análisis de cada licitación',
  'about.feature.ai.desc':
    'Un resumen de cada licitación en español: entiendes de qué va al momento.',
  'about.feature.search.title': 'Búsqueda inteligente',
  'about.feature.search.desc':
    'La búsqueda semántica entiende el sentido de la consulta, no solo las palabras exactas.',
  'about.feature.alerts.title': 'Alertas por email y Telegram',
  'about.feature.alerts.desc':
    'Guarda tu búsqueda y entérate antes que nadie de las nuevas licitaciones que te encajan.',
  'about.audience.title': 'Para quién',
  'about.audience.body':
    'Para empresas, autónomos, contratistas y fabricantes que participan en la contratación pública de España y quieren ver antes que nadie las licitaciones que les interesan.',
  'about.sources.title': 'Fuentes de datos',
  'about.sources.body':
    'Agregamos los datos abiertos de las plataformas oficiales de contratación de España: la Plataforma de Contratación del Sector Público y las plataformas autonómicas que publica. {{brand}} es un servicio independiente y no es un portal de la Administración.',
  'about.press.title': 'Hablan de nosotros',
  'about.contact.title': 'Contacto',
  'about.contact.body': 'Para colaboraciones, suscripciones y soporte, escríbenos:',
  'about.operator': 'Titular del servicio: {{legalName}}.',
  'about.operator.requisitesLink': 'Aviso legal',
  'footer.rights': 'Todos los derechos reservados.',
  // The operator's own tax id label (AI CRM Inc is a US company), not a buyer's NIF.
  'footer.inn': 'Id. fiscal',
  'footer.phone': 'Teléfono',
  // ── Legal pages (conditions / privacy) ────────────────────────────
  'legal.updated': 'Última actualización',
  'legal.requisitesHeading': 'Datos del prestador del servicio',
  // ── Requisites page ────────────────────────────────────────────
  'requisites.pageTitle': 'Aviso legal',
  'requisites.subtitle': 'Datos legales y bancarios del titular del servicio.',
  'requisites.note':
    'Los pagos con tarjeta de la suscripción los recibe AI CRM Inc (EE. UU.) a través de Stripe.',
  // ── Invoice ───────────────────────────────────
  'invoice.pageTitle': 'Factura proforma',
  'invoice.title': 'Factura proforma',
  'invoice.date': 'Fecha',
  'invoice.back': '← A los planes',
  'invoice.print': 'Imprimir / PDF',
  'invoice.seller': 'Proveedor',
  'invoice.buyer': 'Cliente',
  'invoice.buyerName': 'Razón social',
  'invoice.buyerInn': 'NIF',
  'invoice.itemName': 'Descripción del servicio',
  'invoice.itemQty': 'Cant.',
  'invoice.itemPrice': 'Importe',
  'invoice.itemPlan': 'Suscripción {{name}} (anual), acceso al servicio {{brandHost}}',
  'invoice.total': 'Total a pagar',
  'invoice.note':
    'Transfiere el importe indicado a la cuenta del proveedor. El plan {{name}} se activa cuando recibimos el pago. Concepto: suscripción {{name}}, {{brandHost}}.',
  // ── Tender detail extras ───────────────────────────────────────
  'tender.budgetLabel': 'Presupuesto',
  'tender.deadlineLabel': 'Plazo',
  'tender.daysLeftText': 'cierra en {{days}}',
  'tender.nav.description': 'Descripción',
  'tender.nav.procedure': 'Procedimiento',
  'tender.nav.documents': 'Documentos',
  'tender.nav.lotDetails': 'Entrega',
  'tender.identity.platform': 'Plataforma',
  'tender.lotDetails.title': 'Condiciones de entrega',
  'tender.lotDetails.unitPrice': 'Precio unitario',
  'tender.lotDetails.qty': 'Cantidad',
  'tender.lotDetails.unit': 'Unidad de medida',
  'tender.lotDetails.deliveryPlace': 'Lugar de entrega',
  'tender.lotDetails.deliveryTerm': 'Plazo de entrega',
  'tender.lotDetails.advancePct': 'Anticipo',
  'tender.lotDetails.paymentType': 'Condiciones de pago',
  'tender.lotDetails.paymentTermDays': 'Plazo de pago',
  'tender.lotDetails.positions': 'Partidas del lote: {{n}}',
  'tender.lotDetails.incoterms': 'Incoterms',
  'tender.lotDetails.purchaseType': 'Tipo de compra',
  'tender.lotDetails.batchLotCount': 'Lotes en el anuncio',
  // Same words as the catalogue card's «Importe total del expediente» for the same field.
  'tender.lotDetails.batchTotal': 'Importe total del expediente',
  'tender.lotDetails.legalAddress': 'Domicilio del comprador',
  'tender.positions.title': 'Qué se compra',
  'tender.positions.unitPrice': '{{price}} por ud.',
  'tender.positions.total': 'total {{sum}}',
  'tender.positions.showAll': 'Ver todas las partidas ({{n}})',
  'tender.positions.collapse': 'Ver menos',
  'tender.positions.capped': 'Partidas mostradas: {{shown}} de {{n}}',
  'tender.nav.requirements': 'Requisitos',
  'tender.nav.contacts': 'Contacto',
  'tender.nav.integrity': 'Parámetros',
  'tenderIntegrity.title': 'Parámetros del procedimiento',
  'tenderIntegrity.hint':
    'Señales a tener en cuenta: nueva convocatoria, licitador único, garantía, requisitos. Solo hechos, sin valoraciones.',
  'tenderIntegrity.teaserRelisting': 'Nueva convocatoria del lote',
  'tenderIntegrity.teaserDeposit': 'Garantía sobre el presupuesto',
  // How many of the checks above this lot has the data for (`checksCount`) — not what they
  // found. «Comprobaciones en los documentos del lote: 1» read as a riddle (review 21.10).
  'tenderIntegrity.teaserBasis': 'Comprobaciones disponibles para este lote: {{n}}',
  'tenderIntegrity.relisting': 'Nueva convocatoria del procedimiento #{{id}}',
  'tenderIntegrity.prevFailed': 'el anterior quedó desierto',
  'tenderIntegrity.prevParticipants': 'licitadores en él: {{n}}',
  'tenderIntegrity.participants': 'Licitadores registrados: {{n}}',
  'tenderIntegrity.single': 'Único licitador registrado',
  'tenderIntegrity.deposit': 'Garantía del {{pct}} % del presupuesto',
  'tenderIntegrity.median': 'mediana de la sección: {{pct}} %',
  'tenderIntegrity.quals': 'Requisitos excluyentes: {{n}}',
  'tenderIntegrity.qualsShow': 'Ver los requisitos',
  'tenderIntegrity.weights': 'valoración {{tech}}:{{cost}} (técnica:precio)',
  'tenderIntegrity.source': 'Según las actas del procedimiento y el historial de contratos',
  'tender.callAction': 'Llamar',
  'tender.summaryLabel': 'Descripción',
  'tender.detailLabel': 'Descripción',
  'tender.overview.description': 'Descripción',
  'tender.overview.specification': 'Especificación',
  'tender.overview.bidSecurity': 'Garantía provisional',
  'tender.documentTextLabel': 'Texto de los pliegos',
  'tender.noSummary': 'El resumen de esta licitación todavía no está disponible.',
  // ── Tender gate card ───
  'tender.reveal.title': 'Contacto del comprador',
  'tender.reveal.remaining': 'Aperturas que te quedan este mes: {{count}}',
  'tender.reveal.action': 'Ver el contacto',
  'tender.reveal.titleNoContact': 'Anuncio original',
  'tender.reveal.noContactHint':
    'El órgano de contratación no ha publicado contacto para esta licitación. Al abrirla tienes el enlace al anuncio original en la plataforma.',
  'tender.reveal.actionNoContact': 'Ver el original',
  'tender.reveal.pending': 'Abriendo…',
  // es (owner, 2026-10-20): the contact is Pro, as on UZ (metered plan only;
  // Spain sells none — `ipPlan: false`).
  'tender.reveal.ceiling':
    'El presupuesto de esta licitación supera el límite de tu plan. Los contactos de estas licitaciones están en Pro.',
  'tender.reveal.exhausted':
    'Has agotado las aperturas de contacto de este mes; el límite se renueva el día 1. O pásate a Pro, sin límite.',
  'tender.reveal.error': 'No se ha podido mostrar el contacto. Inténtalo de nuevo.',
  'tender.reveal.limitedDay':
    'Has alcanzado el límite diario de aperturas de contacto ({{limit}}). Se restablece a las {{time}}. Si crees que es un error, escribe a soporte.',
  'tender.reveal.limitedMinute':
    'Has abierto demasiados contactos seguidos. Espera un minuto y vuelve a intentarlo. Si crees que es un error, escribe a soporte.',
  'tender.reveal.limitedSupport': 'Escribir a soporte',

  // Bid package (Pro only, behind BID_PACKAGE_ENABLED).
  'tender.bidPackage.title': 'Paquete de oferta',
  'tender.bidPackage.cta': 'Preparar la oferta',
  'tender.bidPackage.statusIdle': 'Sin preparar',
  'tender.bidPackage.statusReady': 'Listo',
  'tender.bidPackage.statusReadyOn': 'Listo · preparado el {{date}}',
  'tender.bidPackage.statsDocs': 'documentos obligatorios: {{n}}',
  'tender.bidPackage.statsGaps': 'huecos: {{n}}',
  'tender.bidPackage.statsProfile': 'ficha completada al {{pct}} %',
  'tender.bidPackage.profileFill': 'Completar la ficha de la empresa →',
  'tender.bidPackage.details': 'Ver detalles',
  'tender.bidPackage.detailsHide': 'Ocultar detalles',
  'tender.bidPackage.gateWarn': 'El paquete saldrá con huecos: ficha completada al {{pct}} %.',
  'tender.bidPackage.gateLink': 'Completar →',
  'tender.bidPackage.building': 'Preparando…',
  'tender.bidPackage.preparing': 'Estamos preparando el documento; te lo enviaremos por email',
  'tender.bidPackage.preparingHint':
    'Puedes cerrar la página: suele tardar entre 10 y 30 segundos. El paquete también quedará aquí.',
  'tender.bidPackage.failed': 'No se ha podido preparar el paquete.',
  'tender.bidPackage.retry': 'Reintentar',
  'tender.bidPackage.error': 'No se ha podido preparar el paquete. Inténtalo de nuevo.',
  'tender.bidPackage.errorNotGranted':
    'El paquete de oferta no está disponible ahora mismo para tu cuenta.',
  'tender.bidPackage.errorMonthlyLimit':
    'Has alcanzado el límite de este mes: 3 paquetes al mes. Los paquetes ya preparados se abren sin límite; el límite se renueva el día 1.',
  'tender.bidPackage.errorGrantLimit':
    'Ya has usado todos los paquetes de tu acceso. Los ya preparados se abren sin límite; escríbenos si necesitas más.',
  'tender.bidPackage.errorRegenCooldown':
    'Este paquete se puede rehacer una vez por hora. La versión actual sigue disponible.',
  'tender.bidPackage.downloadError': 'No se ha podido descargar el archivo. Inténtalo de nuevo.',
  'tender.bidPackage.stale': 'El anuncio ha cambiado después de preparar el paquete.',
  'tender.bidPackage.rebuild': 'Volver a preparar',
  'tender.bidPackage.regenerate': 'Rehacer',
  'tender.bidPackage.ctaClosed': 'Plazo de presentación cerrado',
  'tender.bidPackage.errorTenderClosed':
    'El plazo de presentación de este lote está cerrado: ya no se puede preparar el paquete.',
  'tender.bidPackage.rebuildFailed': 'No se ha podido rehacer; se muestra el documento anterior',
  'tender.bidPackage.warnClosingSoon': 'Queda menos de un día para el cierre.',
  'tender.bidPackage.warnUzMachineTranslated':
    'Parte del texto se ha traducido automáticamente: revísalo antes de presentar.',
  'tender.bidPackage.checkedAgainst': 'Comprobado con los datos de la empresa {{company}}',
  'tender.bidPackage.noCompany':
    'No has indicado el NIF en tu perfil, así que no hemos podido comprobar los requisitos con tu empresa: tendrás que revisarlos todos por tu cuenta.',
  'tender.bidPackage.admissionOk': 'Puedes presentarte',
  'tender.bidPackage.admissionBlocked': 'Hay impedimentos: {{count}}',
  'tender.bidPackage.admissionTitle': 'Comprobación de admisión',
  'tender.bidPackage.deadlineLabel': 'Plazo hasta',
  'tender.bidPackage.methodLabel': 'Forma de presentación',
  'tender.bidPackage.methodUnknown':
    'la plataforma gestiona varios procedimientos: consulta el tipo en la ficha del lote',
  'tender.bidPackage.submitTitle': 'Dónde y cómo presentar',
  'tender.bidPackage.submitEntry': 'Acceso a la plataforma',
  'tender.bidPackage.profileTitle': 'Ficha de la empresa: solo lo que falta',
  'tender.bidPackage.profileLead':
    'Ningún registro publica estos campos y los formularios del paquete los necesitan. Te los pedimos una sola vez.',
  'tender.bidPackage.profileContinue': 'Continuar',
  'tender.bidPackage.profileSaving': 'Guardando…',
  'tender.bidPackage.profileSaveError': 'No se ha podido guardar la ficha. Inténtalo de nuevo.',
  'tender.bidPackage.profileSkipHint':
    'Puedes saltarte cualquier campo: en el documento quedará un hueco visible para que lo rellenes tú.',
  'tender.bidPackage.profileLink': 'El resto de datos de la empresa, en el perfil',
  'tender.bidPackage.groupAuto': 'Comprobado con tus datos',
  'tender.bidPackage.groupAutoHint':
    'Confirmado con el registro y el historial de adjudicaciones. No sustituye la revisión de la mesa de contratación.',
  'tender.bidPackage.groupManual': 'Revísalo tú',
  'tender.bidPackage.groupManualHint':
    'Requisitos obligatorios que solo tú puedes confirmar: certificados, declaraciones responsables, copias compulsadas.',
  'tender.bidPackage.groupSoft': 'Preferencias del comprador',
  'tender.bidPackage.emptyGroup': 'El anuncio no lo indica.',
  'tender.bidPackage.evidence': 'Se acredita con: {{value}}',
  'tender.bidPackage.verdict.ok': 'cumples',
  'tender.bidPackage.verdict.fail': 'no cumples',
  'tender.bidPackage.basis.registration': 'Según el registro: {{value}}',
  'tender.bidPackage.basis.region': 'Comunidad autónoma de la empresa: {{value}}',
  'tender.bidPackage.basis.legal_form': 'Forma jurídica: {{value}}',
  'tender.bidPackage.basis.vat': 'Sujeto pasivo de IVA: {{value}}',
  'tender.bidPackage.basis.experience': 'Adjudicaciones en este sector: {{value}}',
  'tender.bidPackage.documents': 'Documentos de la oferta',
  'tender.bidPackage.mandatory': 'obligatorio',
  'tender.bidPackage.optional': 'opcional',
  'tender.bidPackage.coverLetter': 'Solicitud de participación: borrador',
  'tender.bidPackage.offer': 'Oferta económica: borrador',
  'tender.bidPackage.offerHint': 'No rellenamos los precios: los pones tú.',
  'tender.bidPackage.uzMark': 'revisar',
  'tender.bidPackage.uzMarkHint':
    'El texto se ha traducido automáticamente: revísalo antes de presentar.',
  'tender.bidPackage.colItem': 'Descripción',
  'tender.bidPackage.colQty': 'Cant.',
  'tender.bidPackage.colUnit': 'Ud.',
  'tender.bidPackage.colPrice': 'Precio unitario',
  'tender.bidPackage.download': 'Descargar DOCX',
  'tender.bidPackage.downloading': 'Preparando el archivo…',
  'tender.bidPackage.sendEmail': 'Por email',
  'tender.bidPackage.sendTelegram': 'A Telegram',
  'tender.bidPackage.sending': 'Enviando…',
  'tender.bidPackage.sentEmail': 'Enviado a tu email.',
  'tender.bidPackage.sentTelegram': 'Enviado a Telegram.',
  'tender.bidPackage.sendHint':
    'Si el archivo no se descarga (al navegador integrado de algunas apps le pasa), recíbelo por email.',
  'tender.bidPackage.sendError': 'No se ha podido enviar. Inténtalo de nuevo.',
  'tender.bidPackage.sendNoEmail': 'Tu cuenta no tiene email: añádelo en el perfil.',
  'tender.bidPackage.sendNoTelegram': 'Telegram no está conectado: vincula el bot en tu perfil.',
  'tender.bidPackage.disclaimer':
    'Borrador preparado automáticamente a partir del anuncio y los archivos adjuntos. Antes de presentarlo, compáralo con el original: el licitador es responsable del contenido de su oferta.',

  // ── Bid documentation waitlist (components/bid-waitlist-button.tsx) ──
  'tender.bidWaitlist.cta': 'Preparar la documentación de la oferta (beta)',
  'tender.bidWaitlist.caption': 'Paquete de documentos para presentar oferta a este lote',
  'tender.bidWaitlist.queued': 'Estás en la lista de espera',
  'tender.bidWaitlist.queuedCaption': 'Te daremos acceso y te avisaremos por Telegram',
  'tender.bidWaitlist.error': 'No hemos podido apuntarte a la lista. Inténtalo de nuevo.',
  'tender.bidWaitlist.captionReprompt': 'Completa el cuestionario',
  'tender.bidWaitlist.queuedCaptionEmail': 'Te daremos acceso y te avisaremos por email',

  'tender.bidWaitlistForm.title': 'Documentación para la oferta',
  'tender.bidWaitlistForm.beta': 'beta',
  'tender.bidWaitlistForm.intro':
    'Preparamos el paquete de la oferta a partir de la documentación del lote: requisitos, formularios y borradores. Damos acceso por orden de lista.',
  'tender.bidWaitlistForm.termsTitle': 'Condiciones después de la beta',
  'tender.bidWaitlistForm.termsLead':
    'Si ganas con nuestra documentación, firmamos un contrato y cobramos una comisión sobre el importe adjudicado:',
  // Product decision (es): the fee scale is the owner's Uzbek one in so'm; these
  // euro thresholds are a placeholder until the owner sets Spain's.
  'tender.bidWaitlistForm.tier1Range': 'hasta 100.000 €',
  'tender.bidWaitlistForm.tier1Fee': 'hasta 5 %',
  'tender.bidWaitlistForm.tier2Range': 'hasta 1 M€',
  'tender.bidWaitlistForm.tier2Fee': 'hasta 1 %',
  'tender.bidWaitlistForm.tier3Range': 'más de 1 M€',
  'tender.bidWaitlistForm.tier3Fee': 'hasta 0,5 %',
  'tender.bidWaitlistForm.termsUpload':
    'Después de presentarla, nos subes la oferta presentada: con ella mejoramos la siguiente.',
  'tender.bidWaitlistForm.tinLabel': '{{tin}} de la empresa',
  'tender.bidWaitlistForm.tinPlaceholder': '{{tinDigits}} caracteres',
  'tender.bidWaitlistForm.tinHint': 'con él veremos tus adjudicaciones',
  'tender.bidWaitlistForm.tinMissing': 'Indica el {{tin}} de la empresa ({{tinDigits}} caracteres)',
  'tender.bidWaitlistForm.supplyLabel': 'Qué ofreces',
  'tender.bidWaitlistForm.supplyPlaceholder':
    'mobiliario, material de oficina, reparación de cubiertas',
  'tender.bidWaitlistForm.bidsLabel': 'A cuántas licitaciones te presentas al mes',
  'tender.bidWaitlistForm.bids.0': '0',
  'tender.bidWaitlistForm.bids.1_3': '1–3',
  'tender.bidWaitlistForm.bids.4_10': '4–10',
  'tender.bidWaitlistForm.bids.10_plus': 'más de 10',
  'tender.bidWaitlistForm.preparerLabel': 'Quién prepara ahora la documentación',
  'tender.bidWaitlistForm.preparer.self': 'nosotros mismos',
  'tender.bidWaitlistForm.preparer.employee': 'un empleado',
  'tender.bidWaitlistForm.preparer.broker': 'una consultora',
  'tender.bidWaitlistForm.brokerPercentLabel': 'a qué porcentaje',
  'tender.bidWaitlistForm.lotLabel': 'Importe habitual del lote',
  'tender.bidWaitlistForm.lot.to_100m': 'hasta 10.000 €',
  'tender.bidWaitlistForm.lot.to_1b': 'hasta 100.000 €',
  'tender.bidWaitlistForm.lot.to_10b': 'hasta 1 M€',
  'tender.bidWaitlistForm.lot.over_10b': 'más de 1 M€',
  'tender.bidWaitlistForm.agree': 'Entiendo las condiciones y me interesa',
  'tender.bidWaitlistForm.termsMissing':
    'Marca «Entiendo las condiciones» para apuntarte a la lista',
  'tender.bidWaitlistForm.submit': 'Apuntarme a la lista',
  'tender.bidWaitlistForm.footnote':
    'Ahora no te compromete a nada. Tendremos en cuenta tus respuestas al abrir el acceso.',
  'tender.bidWaitlistForm.doneTelegram': 'Te escribiremos por Telegram cuando abramos el acceso',
  'tender.bidWaitlistForm.doneEmail': 'Te escribiremos por email cuando abramos el acceso',
  'tender.bidWaitlistForm.doneOk': 'Entendido',

  // ── Company data for bids (account area) ──
  'bidProfile.title': 'Datos de la empresa para las ofertas',
  'bidProfile.lead':
    'Firmante, contactos, datos fiscales y bancarios, licencias, experiencia y medios: cada paquete de oferta los usa. Rellénalos una vez: para cada lote solo te preguntaremos lo que falte aquí.',
  'bidProfile.completeness': 'Completado: {{filled}} de {{total}}',
  'bidProfile.save': 'Guardar',
  'bidProfile.saving': 'Guardando…',
  'bidProfile.saved': 'Guardado.',
  'bidProfile.saveError': 'No se ha podido guardar. Inténtalo de nuevo.',
  'bidProfile.loadError': 'No se han podido cargar los datos de la empresa.',
  'bidProfile.skipHint':
    'Puedes dejar cualquier campo vacío: en el documento quedará un hueco visible para que lo rellenes tú.',

  // es (owner, 2026-10-20): the gate lists what Pro opens on the lot — the UZ set.
  // The title promises nothing by itself: the rows are listed only for what the
  // lot has (a feed-1044 lot carries no contact), see TenderGateCard.
  'tender.gate.title': 'Con Pro, en esta licitación tienes:',
  'tender.gate.lockedContacts': 'Contacto del órgano de contratación',
  // Same reason as plans.feature.winnerContacts; rendered on UZ only (TenderGateCard).
  'tender.gate.lockedWinnerContacts': 'Historial de adjudicaciones de las empresas',
  'tender.gate.lockedDocs': 'Pliegos y documentación',
  // Rendered by no gate since 2026-10-20 (it was the AI read of the pliegos).
  'tender.gate.lockedSummary': 'Resumen completo de los pliegos',
  // First row on ES (`features.marketFirstPitch`); documents and contact follow.
  'tender.gate.lockedAnalytics': 'Quién gana, por cuánto y contra quién compites',
  'tender.gate.guest': 'Regístrate gratis: búsquedas guardadas y alertas por email o en Telegram',
  'tender.gate.free':
    'Hazte Pro: quién gana, por cuánto y contra quién compites, y alertas sin límite. También el contacto del órgano y los pliegos.',
  'tender.cta.register': 'Registrarse',
  'tender.cta.pro': 'Activar el plan Pro',
  'tender.cta.contacts': 'Ver el contacto',
  // ── Free full-tender unlock ──
  'freeUnlock.title': 'Desbloqueo gratuito de una licitación',
  'freeUnlock.buttonFree': 'Ver gratis los datos Pro de esta licitación',
  'freeUnlock.remaining': 'Desbloqueos gratuitos restantes: {{n}}',
  'freeUnlock.pending': 'Abriendo…',
  'freeUnlock.error': 'No se ha podido desbloquear la licitación. Inténtalo de nuevo.',
  'freeUnlock.spent': 'Ya has usado el desbloqueo gratuito: hazte Pro',
  'freeUnlock.needTin': 'Añade el NIF de la empresa',
  'freeUnlock.needPhone': 'Confirma tu teléfono en Telegram',
  'freeUnlock.phoneTaken': 'Este número ya se ha usado en otra cuenta',
  'freeUnlock.completeProfile': 'Completar el perfil',
  'freeUnlock.confirmPhone': 'Confirmar el teléfono',
  'freeUnlock.orPro': 'o hazte Pro',
  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.greeting': 'Hola de nuevo',
  'dashboard.nudge.title': 'Añade una categoría y recibe alertas',
  'dashboard.nudge.body':
    'Elige una categoría en el catálogo y guárdala. Te avisaremos por email o en Telegram en cuanto aparezca una licitación nueva que encaje.',
  'dashboard.nudge.cta': 'Añadir categoría',
  'onboarding.title': 'Termina la configuración',
  'onboarding.progress': '{{done}} de {{total}}',
  'onboarding.step.telegram': 'Acceso con Telegram',
  'onboarding.step.telegramHint': 'Conecta Telegram más abajo para recibir alertas.',
  'onboarding.step.search': 'Guarda al menos una búsqueda',
  'onboarding.step.inn': 'Indica el {{tin}} de la empresa',
  'onboarding.step.innHint':
    'Te mostraremos cuántas licitaciones ha ganado tu empresa. El formulario está más abajo en esta página.',
  'onboarding.step.searchCta': 'Añadir',
  // CPV sector picker
  'ikpu.picker.placeholder': 'Busca por nombre de producto o código CPV…',
  'ikpu.picker.searching': 'Buscando…',
  'ikpu.picker.noResults': 'No se ha encontrado nada',
  'ikpu.picker.remove': 'Quitar',
  'ikpu.picker.activeCount': '{{count}} abiertas',
  'ikpu.picker.hint': 'Elige hasta {{max}} sectores',
  'ikpu.picker.max': 'Has elegido el máximo ({{max}}). Quita un sector para añadir otro.',
  'onboarding.niche.title': 'Elige un sector',
  'onboarding.niche.desc':
    'Te enviaremos por email o a Telegram cada licitación nueva que encaje con este sector.',
  'onboarding.niche.save': 'Activar alertas',
  'onboarding.niche.saving': 'Guardando…',
  'onboarding.niche.saveNetworkError':
    'No se ha podido guardar: revisa la conexión. Lo hemos reintentado, pero la conexión se ha cortado.',
  'onboarding.niche.saveError': 'No se ha podido guardar el sector. Inténtalo de nuevo.',
  'onboarding.done.intro': '¡Listo! Alertas activadas.',
  'onboarding.done.sent': 'Te hemos enviado la primera licitación a Telegram',
  'onboarding.done.sentDesc':
    'A partir de ahora, cada licitación nueva que encaje te llegará automáticamente.',
  'onboarding.done.empty': 'Ahora no hay licitaciones abiertas en este sector.',
  'onboarding.done.emptyAdjacent':
    'Te avisaremos en cuanto haya alguna. Sectores parecidos con lotes abiertos:',
  'onboarding.done.close': 'Listo',
  // First-login onboarding pop-up
  'onboarding.modal.title': '¡Te damos la bienvenida a {{brand}}!',
  'onboarding.modal.intro':
    'Configura las alertas para no perderte las licitaciones que te encajan.',
  'onboarding.modal.tgTitle': 'Conecta Telegram',
  'onboarding.modal.tgDesc': 'Recibe alertas de nuevas licitaciones directamente en Telegram.',
  'onboarding.modal.searchTitle': 'Añade una categoría',
  'onboarding.modal.searchDesc':
    'Elige una categoría y guarda la búsqueda: te avisaremos de las nuevas licitaciones que encajen.',
  'onboarding.modal.searchCta': 'Elegir categoría',
  'onboarding.modal.later': 'Más tarde',
  'onboarding.modal.close': 'Cerrar',
  'onboarding.categories.title': 'Elige una categoría',
  'onboarding.categories.desc':
    'Nuevas licitaciones y alertas por email o en Telegram del sector que elijas.',
  'onboarding.categories.searchToggle': '¿No la encuentras? Buscar →',
  'onboarding.categories.search': 'Buscar categoría…',
  'onboarding.categories.searchNoResults': 'No se ha encontrado nada',
  'onboarding.categories.searching': 'Buscando…',
  'onboarding.categories.popular': 'Populares',
  'onboarding.ikpu.label': 'O por código CPV',
  'onboarding.ikpu.hint': 'Empieza a escribir y te mostraremos los sectores exactos',
  'onboarding.categories.section': 'Categorías',
  'onboarding.ikpu.section': 'Sectores exactos',
  'onboarding.categories.more': '{{count}} categorías más',
  'onboarding.saved': 'Guardado: las alertas de nuevas licitaciones de tu sector están activadas.',
  'onboarding.stepOf': 'Paso {{n}} de {{total}}',
  'onboarding.back': '← Atrás',
  'onboarding.tg.cta': 'Entérate antes que nadie de las nuevas licitaciones',
  'onboarding.tg.connectedDone': 'Telegram conectado: todo listo.',
  'onboarding.sample.bot': '{{brand}} bot',
  'onboarding.sample.title': 'Nueva licitación de tu sector',
  'onboarding.sample.tender': '«Suministro de material para un organismo público»',
  'onboarding.sample.tender.construction': '«Obras de reforma integral de un colegio»',
  'onboarding.sample.tender.automotive': '«Suministro de vehículos para el servicio municipal»',
  'onboarding.sample.tender.food': '«Suministro de alimentos para una escuela infantil»',
  'onboarding.sample.tender.consulting': '«Servicio de dirección facultativa de obras»',
  'onboarding.sample.tender.events-culture': '«Organización de un acto institucional»',
  'onboarding.sample.tender.electrical':
    '«Suministro e instalación de un centro de transformación»',
  'onboarding.sample.tender.machinery': '«Suministro de equipos de bombeo»',
  'onboarding.sample.tender.water-environmental':
    '«Renovación de la red de abastecimiento de agua»',
  'onboarding.sample.tender.energy-fuel': '«Suministro de gasóleo»',
  'onboarding.sample.tender.transport-logistics': '«Servicio de transporte de mercancías»',
  'onboarding.sample.tender.financial-legal': '«Servicio de auditoría de cuentas anuales»',
  'onboarding.sample.tender.agriculture': '«Suministro de fertilizantes»',
  'onboarding.sample.tender.chemicals': '«Suministro de pinturas y barnices»',
  'onboarding.sample.tender.office-supplies': '«Suministro de material de oficina»',
  'onboarding.sample.tender.printing-publishing': '«Servicio de impresión de material didáctico»',
  'onboarding.sample.tender.cleaning-sanitation':
    '«Servicio de limpieza de edificios administrativos»',
  'onboarding.sample.meta': '💰 240.000 € · ⏰ quedan 12 días',
  'onboarding.sample.budget': '💰 {{amount}} €',
  'onboarding.sample.daysLeft': '⏰ cierra en {{n}} {{word}}',
  'alerts.unconfirmed':
    'Confirma tu email: hasta entonces solo te llegará la primera selección de licitaciones.',
  'alerts.unconfirmed.cta': 'Confirmar el email',
  'dashboard.alerts.searchesCount': 'Búsquedas guardadas: {{count}}.',
  'emailConfirm.ok': 'Email confirmado: las selecciones te llegarán ahí.',
  'emailConfirm.expired': 'El enlace ha caducado. Confirma el email con un código.',
  'emailConfirm.invalid': 'El enlace no ha funcionado. Confirma el email con un código.',
  'emailConfirm.signIn':
    'Entra en el sitio: puedes confirmar el email en la línea «Confirmar el email» que hay sobre el catálogo.',
  'emailConfirm.close': 'Cerrar',
  'onboarding.categories.pickPrompt': 'Elige una categoría para continuar',
  'onboarding.cta.connect': 'Recibir «{{category}}» en Telegram',
  'onboarding.cta.finish': 'Listo',
  'catalog.nudge.compact':
    'Elige una categoría y guárdala: las nuevas licitaciones te llegarán por email o a Telegram.',
  'dashboard.alerts': 'Alertas',
  'dashboard.alerts.empty': 'No tienes alertas configuradas.',
  'dashboard.alerts.active':
    'Activas: {{count}}. Las nuevas licitaciones que encajan te llegan a Telegram.',
  'dashboard.alerts.activeEmail':
    'Activas: {{count}}. Las nuevas licitaciones que encajan te llegan por email.',
  'dashboard.alerts.activeBoth':
    'Activas: {{count}}. Las nuevas licitaciones que encajan te llegan a Telegram y por email.',
  'dashboard.alerts.allChannelsOff':
    'Búsquedas guardadas: {{count}}, pero todos los canales de aviso están desactivados: no recibirás nada.',
  'dashboard.alerts.connectTg': 'Conecta Telegram más arriba para recibir alertas.',
  'dashboard.alerts.noSearches':
    'Guarda una búsqueda para recibir alertas de nuevas licitaciones de tu sector.',
  'dashboard.notifications': 'Alertas de nuevas licitaciones',
  'dashboard.notifications.hint':
    'Avisos de licitaciones que encajan, en Telegram o por email. Desactívalo para pausar todas las alertas.',
  'dashboard.notifications.email': 'Alertas por email',
  'dashboard.notifications.email.hint':
    'Las nuevas licitaciones que encajan te llegan por email, en un resumen como máximo cada 12 horas.',
  'dashboard.notifications.telegram': 'Alertas en Telegram',
  'dashboard.notifications.telegram.hint': 'Las licitaciones que encajan te llegan a Telegram.',
  'dashboard.notifications.telegram.connectFirst': 'Conecta Telegram más arriba',
  'dashboard.plan': 'Tu plan',
  // es (owner, 2026-10-20): the UZ upgrade reason — contacts and the export.
  'dashboard.plan.free':
    'Plan gratuito: pásate a Pro para abrir los contactos, los pliegos y la exportación.',
  'dashboard.plan.active': 'Plan {{plan}} activo hasta el {{date}}.',
  'dashboard.plan.manage': 'Gestionar la suscripción',
  'dashboard.plan.autoRenewOn': 'Renovación automática activada',
  'dashboard.plan.autoRenewOff': 'Renovación automática desactivada',
  'dashboard.matches.week':
    'Tus búsquedas guardadas han encontrado {{count}} licitaciones nuevas en 7 días.',
  'dashboard.matches.none':
    'En los últimos 7 días no hay coincidencias nuevas para tus búsquedas guardadas.',
  // ── Support / contact-us ───────────────────────────────────────
  'support.trigger': 'Ayuda y soporte',
  'support.title': 'Ayuda y soporte',
  'support.desc': 'Cuéntanos el problema o la idea y te responderemos por email.',
  'support.dashboard.desc': '¿Tienes una pregunta o una sugerencia? Escríbenos y te ayudamos.',
  'support.emailPrefix': 'Escríbenos al email:',
  'support.telegramPrefix': 'O escríbenos por Telegram:',
  'support.phonePrefix': 'O llámanos:',
  'support.categoryLabel': 'Categoría',
  'support.category.improvement': 'Mejora',
  'support.category.bug': 'Error del sistema',
  'support.category.subscription': 'Suscripción',
  'support.category.notification': 'Problema con una alerta',
  'support.category.other': 'Otro',
  'support.messageLabel': 'Mensaje',
  'support.messagePlaceholder': 'Cuéntanos qué ha pasado o qué podemos mejorar…',
  'support.emailLabel': 'Email para la respuesta',
  'support.submit': 'Enviar',
  'support.sending': 'Enviando…',
  'support.success': '¡Gracias! Te responderemos pronto.',
  'support.done': 'Listo',
  'support.error': 'No se ha podido enviar. Inténtalo de nuevo.',
  'support.invalid':
    'Elige una categoría, escribe un mensaje (mínimo 5 caracteres) y un email válido.',
  'support.close': 'Cerrar',
  // ── Billing return (post-payment landing) ──────────────────────
  'billing.return.pageTitle': 'Pago recibido',
  'billing.return.title': '¡Gracias por tu pago!',
  'billing.return.body':
    'Hemos recibido el pago y lo estamos procesando. Tu plan se activará automáticamente en unos minutos.',
  'billing.return.bodyActive': 'El pago se ha completado: ya tienes acceso.',
  'billing.return.planActive': 'Plan {{plan}} activo',
  'billing.return.paidUntil': 'Acceso hasta el {{date}}',
  'billing.return.nextCharge': 'Renovación automática activada · próximo cobro el {{date}}',
  'billing.return.noteActive':
    'Los detalles de la suscripción y del pago están en tu cuenta. Si tienes dudas, escríbenos a {{brandEmail}}.',
  'billing.return.attachTitle': 'Activa la renovación automática',
  'billing.return.attachBody':
    'El acceso terminará el {{date}}. Vincula una tarjeta y la suscripción se renovará sola, sin volver a pagar a mano.',
  'billing.return.attachCta': 'Vincular tarjeta',
  'billing.return.orderRef': 'Número de pedido',
  'billing.return.ctaCatalog': 'Ir a las licitaciones',
  'billing.return.ctaTender': 'Volver a la licitación',
  'billing.return.ctaDashboard': 'Ir a mi cuenta',
  'billing.return.note':
    'Si el plan no se activa en unos minutos, escríbenos a {{brandEmail}} y te ayudamos.',
  // ── Billing return: the payment was refused ────────────────────
  'billing.return.failedPageTitle': 'El pago no se ha completado',
  'billing.return.failedTitle': 'El pago no se ha completado',
  'billing.return.failedBody':
    'El banco ha rechazado el pago o se ha cancelado: la suscripción no se ha activado. Puedes intentarlo de nuevo con la misma tarjeta o con otra.',
  'billing.return.failedReasonFunds': 'La tarjeta no tenía saldo suficiente.',
  'billing.return.failedReasonCard': 'El banco no ha aceptado los datos de la tarjeta.',
  'billing.return.failedReason3ds': 'No se ha superado la verificación 3-D Secure.',
  'billing.return.failedReasonExpired': 'La tarjeta ha caducado.',
  'billing.return.failedRetry': 'Intentarlo de nuevo',
  'billing.return.failedNote':
    'Si aun así se te ha cobrado, escríbenos a {{brandEmail}} y lo revisamos.',
  // «No hemos recibido ningún pago» — /billing/return with nothing paid or pending (`features.neutralBillingReturn`, D8).
  'billing.return.noPaymentTitle': 'No hemos recibido ningún pago',
  'billing.return.noPaymentBody': 'Si has pagado, llegará en unos minutos; si no, vuelve a Planes.',
  'billing.return.noPaymentCta': 'Volver a Planes',
  // ── Team seats ─────────────────────────────────────────────────
  'team.pageTitle': 'Equipo',
  'team.subtitle': 'Acceso a Pro para tu equipo con una sola suscripción.',
  'team.seats': 'Puestos ocupados: {{used}} de {{limit}}',
  'team.role.owner': 'Titular',
  'team.role.member': 'Miembro',
  'team.joined': 'En el equipo desde',
  'team.rename': 'Cambiar el nombre',
  'team.renameLabel': 'Nombre del equipo',
  'team.renameSave': 'Guardar',
  'team.renameCancel': 'Cancelar',
  'team.invite': 'Invitar',
  'team.inviteTitle': 'Enlace de invitación',
  'team.inviteHint':
    'Envía este enlace a la persona de tu equipo, por ejemplo por email. Es de un solo uso y vale 7 días.',
  'team.inviteCopy': 'Copiar',
  'team.inviteCopied': 'Copiado',
  'team.inviteClose': 'Cerrar',
  'team.remove': 'Quitar',
  'team.removeConfirm': '¿Quitar a esta persona del equipo? Perderá el acceso a Pro al momento.',
  'team.invitesTitle': 'Invitaciones sin usar',
  'team.inviteExpires': 'Válida hasta el {{date}}',
  'team.inviteOutstanding': 'Enlace activo',
  'team.revoke': 'Revocar',
  'team.revokeConfirm':
    '¿Revocar la invitación? El enlace dejará de funcionar al momento y ya no servirá para entrar en el equipo.',
  'team.leave': 'Salir del equipo',
  'team.leaveConfirm':
    '¿Salir del equipo? Perderás al momento el acceso a Pro de la suscripción del equipo. Solo podrás volver con una nueva invitación.',
  'team.disband': 'Disolver el equipo',
  'team.disbandConfirm':
    '¿Disolver el equipo? Miembros del equipo: {{count}}. Todos perderán el acceso a Pro al momento y las invitaciones sin usar dejarán de funcionar. No se puede deshacer.',
  'team.noSeats': 'Todos los puestos están ocupados. Libera uno para invitar a alguien más.',
  'team.tooManyInvites':
    'Hay demasiados enlaces de invitación sin usar. Revoca uno en la lista de arriba para crear otro.',
  'team.create': 'Crear equipo',
  'team.emptyTitle': 'Acceso en equipo',
  'team.emptyBody': 'El plan Business incluye 3 puestos: tú y dos personas de tu equipo.',
  'team.planRequired': 'El acceso en equipo está disponible en el plan Business.',
  'team.viaBadge': 'Acceso del equipo «{{name}}»',
  'team.error': 'No se ha podido cargar el equipo. Inténtalo más tarde.',
  'team.genericError': 'No se ha podido completar la acción. Inténtalo de nuevo.',
  // ── Team seats: /join/:token ──────────────────────────────────
  'join.title': 'Invitación a un equipo',
  'join.signedOut': 'Entra o regístrate para aceptar la invitación.',
  'join.accept': 'Aceptar la invitación',
  'join.accepting': 'Aceptando…',
  'join.success': '¡Listo! Ya estás en el equipo «{{name}}».',
  'join.goToTeam': 'Ir al equipo',
  'join.ownSubscription':
    'Ya tienes tu propia suscripción hasta el {{date}}. Seguirá siendo la principal: tu puesto en el equipo empezará a funcionar cuando termine.',
  'join.ownSubscriptionNoDate':
    'Ya tienes tu propia suscripción. Seguirá siendo la principal: tu puesto en el equipo empezará a funcionar cuando termine.',
  'join.alreadyMember': 'Ya estás en el equipo «{{name}}».',
  'join.invitedBy': 'Te invita: {{name}}',
  // es (owner, 2026-10-20): a seat opens what Pro opens — the UZ set.
  'join.teamPlan':
    'Plan del equipo: {{plan}}. Tendrás los contactos de los compradores, los pliegos y la analítica',
  'join.expiresAt': 'El enlace es válido hasta el {{date}}',
  'join.error.invite_not_found': 'El enlace no es válido.',
  'join.error.invite_expired': 'El enlace ha caducado. Pide uno nuevo.',
  'join.error.invite_used': 'El enlace ya se ha usado.',
  'join.error.invite_revoked': 'La invitación se ha revocado.',
  'join.error.no_seats': 'No quedan puestos libres en el equipo.',
  'join.error.already_in_team':
    'Ya formas parte de un equipo. Un miembro puede salir de él en la página «Equipo», y el titular puede disolver allí su equipo. Después, vuelve a abrir la invitación.',
  'join.error.generic': 'No se ha podido aceptar la invitación. Inténtalo más tarde.',
  'notFound.title': 'Página no encontrada',
  'notFound.body':
    'Puede que haya una errata en la dirección o que la página ya no exista; por ejemplo, que la licitación se haya retirado.',
  'notFound.action': 'Volver al catálogo',
  'routeError.badge': 'Error',
  'routeError.title': 'La página no se ha abierto',
  'routeError.body': 'Algo ha fallado al cargar. Recarga la página o vuelve al catálogo.',
  // ── Kind tabs (goods / works / services filter) ──────────────────
  'catalog.kind.all': 'Todas',
  'catalog.kind.goods': 'Suministros',
  'catalog.kind.works': 'Obras',
  'catalog.kind.services': 'Servicios',
  'catalog.kind.aria': 'Tipo de contrato',
  'catalog.kind.ariaLabel': '{{label}}, {{count}} licitaciones',
  'company.title': '¿Nombre o {{tin}} de tu organización?',
  'company.subtitle':
    'Te mostraremos lo que tu empresa ya ha ganado o cuántas licitaciones hay abiertas ahora en tu sector.',
  'company.benefit.wins': 'Te mostraremos cuántas licitaciones ha ganado ya tu empresa',
  'company.benefit.alerts':
    'Si tiene adjudicaciones, activaremos alertas de lo que ya has suministrado, sin configurar filtros',
  'company.benefit.plan':
    'Te ayudamos a no perderte las licitaciones de tu comunidad autónoma y tu sector',
  'company.card.change': 'Cambiar',
  'company.card.activity': 'Actividad:',
  'company.card.status': 'Estado:',
  'company.card.region': 'Comunidad autónoma:',
  'company.card.unknownName': 'Empresa no identificada',
  // A company saved by its typed name, no NIF yet (`keepsTypedName`).
  'company.card.noTin': '{{tin}} sin indicar: añádelo y te diremos lo que ha ganado tu empresa',
  'company.card.notFound':
    'No hemos encontrado ninguna empresa con ese NIF. Revisa el número o, si es correcto, déjalo así: puede que aún no tengamos sus datos.',
  // A NIF or, with none, the name typed (`keepsTypedName`) — both are saved.
  'company.savedTitle': 'Empresa guardada',
  'company.done': 'Listo',
  'settings.title': 'Ajustes',
  'settings.section.company': 'Empresa',
  'settings.section.notifications': 'Alertas',
  'settings.section.subscription': 'Suscripción',
  'settings.section.account': 'Cuenta',
  'settings.summary.company': 'Empresa',
  'settings.summary.noCompany': 'NIF sin indicar',
  'settings.summary.plan': 'Plan',
  'settings.summary.planFree': 'Gratuito',
  // es (owner, 2026-10-20): what the free plan lacks, as on UZ — the contacts.
  'settings.summary.planFreeHint': 'Sin contactos de los compradores',
  'settings.summary.alerts': 'Alertas',
  'settings.summary.alertsOn': 'Activadas',
  'settings.summary.alertsOff': 'Desactivadas',
  'settings.summary.alertsOffHint': 'Telegram no conectado',
  'settings.account.email': 'Email',
  'settings.account.emailMissing': 'sin indicar',
  'settings.account.emailVerified': 'Confirmado',
  'settings.account.support': 'Ayuda y soporte',
  // RGPD art. 17 (QA 22.10 F4): followed by the support address, as a mailto link.
  'settings.account.delete': 'Para eliminar tu cuenta y tus datos, escríbenos a',
  'settings.account.attachEmail.hint':
    'No has indicado email: no tenemos dónde enviarte facturas ni códigos de acceso',
  'settings.account.attachEmail.cta': 'Añadir email',
  'settings.account.attachEmail.label':
    'Introduce tu email y te enviaremos un código de confirmación',
  'settings.account.attachEmail.codeLabel': 'Código enviado a {{email}}. Introdúcelo:',
  'settings.account.attachEmail.send': 'Enviar código',
  'settings.account.attachEmail.confirm': 'Confirmar',
  'settings.account.attachEmail.cancel': 'Cancelar',
  'settings.account.attachEmail.done': 'Email vinculado',
  'settings.account.attachEmail.error':
    'No se ha podido enviar el código. Revisa la dirección e inténtalo de nuevo.',
  'settings.account.attachEmail.badCode': 'El código no es válido o ha caducado. Pide uno nuevo.',
  'settings.account.attachEmail.alreadySet': 'Esta cuenta ya tiene un email real.',
  'settings.account.attachEmail.mergeRefused':
    'Esta cuenta de Telegram tiene una suscripción: escribe a soporte y uniremos las cuentas a mano.',
  'company.tinLabel': 'NIF',
  'company.tinPlaceholder': '{{digits}} caracteres',
  'company.lookup.label': 'Introduce el {{tin}} o el nombre de tu organización',
  'company.lookup.placeholder': 'Nombre o {{tin}} de {{digits}} caracteres',
  'company.lookup.searching': 'Buscando…',
  // es keeps a typed name (`companyIdForm.keepsTypedName`): «Continuar» saves it.
  'company.lookup.empty':
    'No la encontramos en el registro: pulsa «Continuar» y la guardaremos con este nombre',
  'company.lookup.emptyNoSkip':
    'No la encontramos en el registro: pulsa «Continuar» y la guardaremos con este nombre',
  'company.lookup.tinValue': '{{tin}} {{value}}',
  'company.lookup.wins.one': '{{n}} adjudicación en 2 años',
  'company.lookup.wins.few': '{{n}} adjudicaciones en 2 años',
  'company.lookup.wins.many': '{{n}} adjudicaciones en 2 años',
  'company.submit': 'Continuar',
  'company.checking': 'Comprobando…',
  'company.later': 'Más tarde',
  'company.saved': 'NIF guardado.',
  'company.foundWins': 'Hemos encontrado {{n}} {{word}} que ha ganado tu empresa',
  'company.foundSector': 'En tu sector hay ahora {{n}} {{word}} en plazo',
  'company.foundRegion': 'En tu comunidad autónoma hay ahora {{n}} {{word}} en plazo',
  // A company saved by the name the reader typed (`companyIdForm.keepsTypedName`).
  'company.savedName':
    'Hemos guardado tu empresa: «{{name}}». Cuando tengas el {{tin}} a mano, añádelo en Ajustes y te diremos lo que ha ganado.',
  'company.savedWithName': '{{tin}} guardado con el nombre «{{name}}».',
  // {{what}} is «12 contratos (3 licitaciones y 9 menores)» (company-payoff.tsx).
  'company.foundAwards': 'Tu empresa ha ganado {{what}}{{sector}}.',
  'company.foundAwards.sector': ', sobre todo en {{name}}',
  'company.foundAwards.open': 'Ahora hay {{count}} {{word}} en plazo en ese sector.',
  'company.error.invalidTin': 'Revisa el NIF: debe tener {{digits}} caracteres',
  // Nine characters, refused: the control character is what failed (QA 22.10, C9/F2).
  'company.error.invalidControl': 'Revisa el NIF: la letra de control no coincide',
  'company.error.changeLimit': 'Hoy has cambiado el NIF demasiadas veces. Inténtalo mañana.',
  'company.error.generic': 'No se ha podido guardar. Inténtalo de nuevo.',
  // «My company»: claim-your-company-profile flow.
  'claim.card.title': 'Perfil de empresa',
  'claim.card.subtitle':
    'Vincula el perfil de tu empresa a tu cuenta para recibir selecciones de licitaciones que encajen.',
  'claim.card.cta': 'Vincular el perfil de la empresa',
  'claim.card.needsTin': 'Primero indica el NIF de la empresa en la sección de arriba.',
  'claim.preview.wins':
    'Hemos encontrado {{n}} {{word}} que ha ganado esta empresa. ¿Vinculas el perfil y recibes alertas de nuevas licitaciones que encajen?',
  'claim.preview.none':
    'Todavía no hay historial de adjudicaciones, pero puedes vincular el perfil y recibir selecciones de nuevas licitaciones.',
  'claim.preview.confirm': 'Sí, vincular',
  'claim.preview.cancel': 'Cancelar',
  'claim.linked.title': 'Perfil vinculado',
  'claim.linked.wins': '{{n}} {{word}} en el historial de adjudicaciones',
  'claim.linked.note': 'Alertas de licitaciones que encajan activadas.',
  'claim.conflict.title': 'Este NIF ya está vinculado a otra cuenta',
  'claim.conflict.body':
    'Si es tu empresa y has perdido el acceso a esa cuenta, escribe a soporte y lo resolvemos.',
  'claim.error': 'No se ha podido completar la acción. Inténtalo de nuevo.',
  'claim.retry': 'Reintentar',
  'phone.title': 'Déjanos tu teléfono',
  'phone.subtitle':
    'Te contactaremos solo por temas de tu suscripción. Nada de llamadas comerciales.',
  'phone.label': 'Teléfono',
  'phone.save': 'Guardar',
  'phone.saving': 'Guardando…',
  'phone.thanks': 'Gracias, teléfono guardado.',
  'watch.follow': 'Seguir a la empresa',
  'watch.following': 'Siguiendo',
  'watch.hintWinner': 'Te avisaremos en el resumen cuando esta empresa gane un contrato nuevo.',
  'watch.hintBuyer':
    'Te avisaremos en el resumen cuando este comprador publique un contrato nuevo.',
  'watch.proRequired': 'El seguimiento de empresas está disponible en Pro.',
  'watch.limitReached':
    'Has llegado al límite de 50 organizaciones. Deja de seguir una para añadir otra.',
  'watch.list.title': 'Empresas que sigues',
  'watch.list.hint':
    'Los nuevos contratos y licitaciones de estas organizaciones llegan a tu resumen.',
  'watch.list.empty':
    'Todavía nada. Pulsa «Seguir a la empresa» en la página de un comprador o de una empresa.',
  'watch.list.unfollow': 'Dejar de seguir',
  'watch.list.kindBuyer': 'comprador',
  'watch.list.kindWinner': 'empresa',
  'saved.categoriesLabel': 'Categorías de la búsqueda',
  'saved.categoryAdd': 'Añadir categoría, CPV o sector',
  'saved.categoryRemove': 'Quitar la categoría',
  'saved.ikpuLabel': 'Códigos CPV de la búsqueda',
  'saved.ikpuRemove': 'Quitar el código CPV',
  'saved.nicheLabel': 'Sectores de la búsqueda',
  'saved.nicheRemove': 'Quitar el sector',
  'saved.regionLabel': 'Comunidades autónomas',
  'saved.kindLabel': 'Tipo de contrato',
  'saved.textLabel': 'Palabras en la descripción',
  'saved.textPlaceholder': 'bomba',
  'saved.textHint': 'Basta con una de estas palabras.',
  'saved.textHintCodes':
    'Con códigos CPV elegidos, las palabras no filtran: te llegan todos los lotes de esos códigos.',
  'saved.anyRegion': 'Todas las comunidades',
  'saved.includeSupplyLabel': 'Enviar también solicitudes de oferta',
  'saved.includeSupplyHint': 'Solicitudes sin presupuesto anunciado con este filtro.',
  'saved.customerLabel': 'Comprador',
  'saved.customerPlaceholder': 'Nombre de la organización',
  'saved.customerHint': 'Solo recibirás alertas de las licitaciones de este comprador.',
  'saved.customerRemove': 'Quitar el comprador',

  // ── AI onboarding ────────────────────────────────
  'aiOnboarding.step1.title': '¿Qué ofreces?',
  'aiOnboarding.step1.desc':
    'Qué haces, dónde trabajas y desde qué importe, con tus palabras. Con eso elegimos los códigos CPV, las comunidades y las palabras de tus alertas.',
  'aiOnboarding.step1.placeholder':
    'Ej.: limpieza de oficinas y colegios en Madrid y Toledo, contratos desde 20.000 €',
  'aiOnboarding.step1.example':
    'Otros ejemplos: «mantenimiento de alumbrado público en Andalucía», «suministro de material de oficina».',
  'aiOnboarding.step1.or': 'o',
  'aiOnboarding.step1.tinLabel': 'Indica el {{tin}} de la empresa',
  'aiOnboarding.step1.tinHint': 'Tomaremos los códigos de tus contratos anteriores.',
  'aiOnboarding.step1.submit': 'Buscar',
  'aiOnboarding.step1.loading': 'Buscando…',
  'aiOnboarding.step1.manual': 'elegir la categoría a mano',
  // Step 1's hints (`features.aiCompanyStep`, QA 22.10 C1/C4/C5): why «Buscar» is
  // off, and a NIF typed as the description sent to its own step without a 422.
  'aiOnboarding.step1.needWords': 'Describe con 3 palabras o más',
  'aiOnboarding.step1.nifHint': 'El NIF se pide en el paso siguiente.',
  'aiOnboarding.step1.nifGo': 'Ir al paso del NIF',
  'aiOnboarding.step1.nifNoStep': 'Aquí no hace falta el NIF: describe qué ofreces.',
  // The way back to «¿Qué ofreces?» (`features.aiOnboardingReentry`, QA 22.10 C11):
  // /app/alerts, the catalogue's first-ask card and its empty result.
  'aiOnboarding.reentry': 'Configurar mis alertas con IA',
  // As short as ru's «2 из 2 ИИ-поисков»: the counter does not shrink, and the
  // long form squeezed the examples beside it into a 12-character column on a
  // 360 px phone (QA 20.10).
  'aiOnboarding.quota.counter': '{{left}} de {{limit}} búsquedas IA',
  'aiOnboarding.quota.left': 'Te quedan {{n}} {{word}}. En Pro, 15 al mes.',
  'aiOnboarding.quota.word.one': 'búsqueda con IA',
  'aiOnboarding.quota.word.few': 'búsquedas con IA',
  'aiOnboarding.quota.word.many': 'búsquedas con IA',
  'aiOnboarding.step2.title': 'Revisa el resultado',
  'aiOnboarding.step2.byAi': 'Propuesto por la IA a partir de tu descripción: revisa los códigos.',
  'aiOnboarding.row.categories': 'Categorías',
  'aiOnboarding.row.subject': 'Categorías, CPV y sectores',
  'aiOnboarding.row.ikpu': 'Códigos CPV',
  'aiOnboarding.row.niches': 'Sectores',
  'aiOnboarding.row.keywords': 'Palabras',
  'aiOnboarding.row.regions': 'Comunidades autónomas',
  'aiOnboarding.row.excludes': 'Excluir',
  // Label-first, so «1» needs no singular: «1 nuevos al día» was on screen (QA 20.10).
  'aiOnboarding.counts': 'Lotes abiertos ahora: {{open}} · nuevos al día: {{perDay}}',
  'aiOnboarding.locked': 'otros {{n}} {{word}}, en Pro',
  'aiOnboarding.code.one': 'código',
  'aiOnboarding.code.few': 'códigos',
  'aiOnboarding.code.many': 'códigos',
  'aiOnboarding.save': 'Guardar y recibir alertas',
  'aiOnboarding.saving': 'Guardando…',
  'aiOnboarding.saveError': 'No se ha podido guardar la búsqueda. Inténtalo de nuevo.',
  'aiOnboarding.edit': 'Cambiar',
  'aiOnboarding.chipRemove': 'Quitar: {{label}}',
  'aiOnboarding.chipAdd': 'Añadir: {{label}}',
  'aiOnboarding.addWord': 'añadir',
  'aiOnboarding.aiMark': 'Rellenado por la IA',
  'aiOnboarding.notUnderstood.title': 'No he entendido la descripción',
  'aiOnboarding.notUnderstood.desc':
    'Concreta qué ofreces, por ejemplo «bombas y sus recambios». O elige la categoría a mano.',
  'aiOnboarding.notUnderstood.refine': 'Concretar',
  'aiOnboarding.notUnderstood.manual': 'Elegir categoría',
  // A NIF alone with no award in our base (`ai_tin_unknown`, 21.10): our gap, not the
  // reader's — ask the question, never «no he entendido».
  'aiOnboarding.tinUnknown.desc':
    'No encontramos contratos anteriores con este {{tin}}. Cuéntanos qué ofreces y lo buscamos.',
  'aiOnboarding.broad.title': 'Demasiado amplio: {{n}} lotes abiertos',
  'aiOnboarding.broad.desc':
    'Las alertas incluirían casi todo el catálogo. Añade una palabra o una comunidad autónoma para afinar.',
  'aiOnboarding.broad.narrow': 'Afinar',
  'aiOnboarding.broad.keep': 'dejarlo así',
  'aiOnboarding.broad.budgetChip': 'desde {{amount}}',
  'aiOnboarding.none.title': 'Ahora no hay lotes que encajen',
  'aiOnboarding.none.desc':
    'Guardaremos la búsqueda y te avisaremos en cuanto salga el primer lote. Con búsquedas así suele pasar 1 o 2 veces al mes.',
  'aiOnboarding.none.save': 'Guardar y esperar',
  'aiOnboarding.none.widen': 'Ampliar',
  'aiOnboarding.widened.title': 'Hemos ampliado la búsqueda para que te lleguen lotes',
  'aiOnboarding.widened.desc':
    'Con la búsqueda exacta hay ahora estos lotes abiertos: {{n}}. Qué hemos cambiado:',
  'aiOnboarding.widened.class':
    'códigos ampliados a su clase CPV: llegarán también partidas cercanas del mismo producto',
  'aiOnboarding.widened.category':
    'hemos añadido una categoría: llegarán sus lotes con las palabras de la búsqueda',
  'aiOnboarding.widened.budget': 'sin límite inferior de presupuesto',
  'aiOnboarding.widened.regions': 'en toda España, sin comunidad autónoma',
  'aiOnboarding.widened.restore': 'Recuperar:',
  'aiOnboarding.quotaOut.title': 'Se han acabado las búsquedas con IA',
  'aiOnboarding.quotaOut.desc':
    'Has usado las dos búsquedas gratuitas. Puedes montar la búsqueda a mano o pasarte a Pro, con 15 búsquedas al mes.',
  'aiOnboarding.quotaOut.descMonth':
    'Has usado las búsquedas de este mes. Puedes montar la búsqueda a mano; las nuevas llegarán a principio de mes.',
  'aiOnboarding.quotaOut.manual': 'Montarla a mano',
  'aiOnboarding.quotaOut.pro': 'Hazte Pro',
  'aiOnboarding.error.title': 'La IA no ha respondido',
  'aiOnboarding.error.desc':
    'El modelo tarda más de lo normal o ha devuelto un error: abrimos la selección de categoría habitual y el intento no cuenta.',
  'aiOnboarding.error.manual': 'Elegir categoría',
  'aiOnboarding.error.retry': 'Reintentar',
  'aiOnboarding.manualFooter':
    'Siempre puedes elegir la categoría a mano: la IA no es el único camino.',
  'aiOnboarding.fill.title': 'Describe lo que necesitas y la IA rellenará los campos',
  'aiOnboarding.fill.placeholder':
    'necesito bombas y motores eléctricos, Madrid y alrededores, desde 20.000 €',
  'aiOnboarding.fill.submit': 'Rellenar con IA',
  'aiOnboarding.fill.filled': 'rellenado por la IA: puedes cambiarlo como siempre',
  // ── Pay from a computer ─────────────
  'plans.payFromDesktop': 'Pagar desde el ordenador',
  'plans.payFromDesktopSending': 'Enviando el enlace…',
  'plans.payLinkSentTelegram': 'Enlace enviado a Telegram, válido 24 horas',
  'plans.payLinkSentEmail': 'Enlace enviado a tu email, válido 24 horas',
  'plans.payLinkNoChannel': 'No tenemos dónde enviar el enlace: añade un email en tu perfil',
  'plans.payLinkNoChannelCta': 'Añadir email',
  'plans.payLinkError': 'No se ha podido enviar el enlace. Inténtalo de nuevo',
  // Pay by phone (an Uzbek payment-app flow; kept neutral, no Uzbek payee named).
  'plans.sms.hint':
    'En el navegador de {{app}} el pago con tarjeta no funciona. Te enviaremos una solicitud de pago a tu número para que pagues en {{apps}} en un minuto',
  'plans.sms.or': ' o ',
  'plans.sms.phoneLabel': 'Número de teléfono',
  'plans.sms.pickGateway': 'Con qué app vas a pagar',
  'plans.sms.send': 'Enviar la solicitud de pago a mi número',
  'plans.sms.sending': 'Enviando la solicitud…',
  'plans.sms.sentTitle': '✓ Solicitud enviada al {{phone}}',
  'plans.sms.sentBody':
    'Abre la app {{gateway}}: ahí tienes la solicitud de pago. El acceso se activará automáticamente; puedes cerrar esta página',
  'plans.sms.sentPaymeSms': 'También recibirás un SMS con el enlace de pago.',
  'plans.sms.waiting': 'Esperando el pago…',
  'plans.sms.resendPrompt': '¿No te ha llegado en 2 minutos?',
  'plans.sms.resend': 'Enviar de nuevo',
  'plans.sms.resendIn': 'Enviar de nuevo dentro de {{time}}',
  'plans.sms.switchGateway': 'Pagar con {{gateway}}',
  'plans.sms.paidTitle': 'Pagado: {{name}} activo',
  'plans.sms.goTenders': 'Ir a las licitaciones',
  'plans.sms.fallbackHosted': 'Pagar en la página de pago',
  'plans.sms.seconds': '{{n}} s',
  'plans.sms.minutes': '{{n}} min',
  'plans.sms.hours': '{{n}} h',
  'plans.sms.err.invalid_phone': 'Introduce el número: +34 y 9 cifras',
  'plans.sms.err.no_gateway_account':
    'Este número no está registrado en {{gateway}}. Elige otra app u otro número',
  'plans.sms.err.gateway_declined': '{{gateway}} no ha aceptado la solicitud. Prueba con otra app',
  'plans.sms.err.provider_unavailable': '{{gateway}} no está disponible ahora. Elige otra app',
  'plans.sms.err.rate_limited': 'Demasiadas solicitudes seguidas. Inténtalo dentro de {{time}}',
  'plans.sms.err.gateway_error': '{{gateway}} no ha respondido. Inténtalo de nuevo',
  'plans.sms.err.generic': 'No se ha podido enviar la solicitud. Inténtalo de nuevo',

  // ── /pay/:token ─────────────────────────────────────────
  'payLink.pageTitle': 'Pago',
  'payLink.loading': 'Abriendo el pago…',
  'payLink.title': 'Pago desde el ordenador',
  'payLink.lead': 'El pedido ya está listo: pulsa el botón para ir a la página de pago.',
  'payLink.cta': 'Ir al pago',
  'payLink.onceNote':
    'El enlace se abre una sola vez. La página de pago se abrirá en esta ventana.',
  'payLink.backToPlans': 'Volver a los planes',
  'payLink.openPlans': 'Abrir los planes',
  'payLink.used.title': 'El enlace ya se ha abierto',
  'payLink.used.body':
    'Solo vale una vez. Si el pago no se completó, abre los planes y empieza de nuevo: no se te ha cobrado.',
  'payLink.notFound.title': 'El enlace no es válido',
  'payLink.notFound.body':
    'Es válido 24 horas. Puede que el pedido ya esté pagado o cancelado. Abre los planes y empieza de nuevo.',
  'payLink.receipt.paidTitle': 'Pago completado',
  'payLink.receipt.paidBody': 'Ya tienes acceso. Entra en tu cuenta para empezar.',
  'payLink.receipt.pendingTitle': 'Esperando la confirmación del pago',
  'payLink.receipt.pendingBody':
    'El banco todavía está confirmando el pago. Recarga la página dentro de un minuto o simplemente entra en tu cuenta: el acceso se activará solo.',
  'payLink.receipt.failedTitle': 'El pago no se ha completado',
  'payLink.receipt.failedBody': 'No se te ha cobrado. Puedes intentarlo de nuevo.',
  'payLink.receipt.paidAt': 'Pagado el {{date}}',
  'payLink.receipt.loginTelegram': 'Entraste con Telegram {{hint}}',
  'payLink.receipt.loginTelegramPlain': 'Entraste con Telegram',
  'payLink.receipt.loginEmail': 'Entraste con el email {{hint}}',
  'payLink.receipt.loginEmailPlain': 'Entraste con email',
  'payLink.receipt.login': 'Entrar en la cuenta',
  // ── /zayavka: lead form (conversionLandings is off in Spain) ──
  'zayavka.pageTitle': 'Solicitud: encontramos licitaciones para tu producto',
  'zayavka.metaDescription':
    'Tres pasos y te buscamos las licitaciones públicas abiertas de tu sector.',
  'zayavka.progress': '{{n}} de {{total}}',
  'zayavka.back': '← Atrás',
  'zayavka.next': 'Siguiente',
  'zayavka.s1.title': '¿Qué ofreces?',
  'zayavka.s1.search': 'Producto, servicio o código CPV',
  'zayavka.s1.keyword': 'Buscar «{{q}}»',
  'zayavka.s1.loading': 'Contando los lotes abiertos…',
  'zayavka.s1.summary': '{{count}} {{lots}} por {{sum}} €',
  'zayavka.s1.summaryNoSum': '{{count}} {{lots}}',
  'zayavka.s1.none': 'Ahora no hay lotes abiertos',
  'zayavka.lots.one': 'lote abierto',
  'zayavka.lots.few': 'lotes abiertos',
  'zayavka.lots.many': 'lotes abiertos',
  'zayavka.lot.until': 'hasta el {{date}}',
  'zayavka.lot.noBudget': 'Presupuesto no indicado',
  'zayavka.lot.announced': 'Pronto se abre el plazo',
  'zayavka.s2.title': 'Un poco sobre ti',
  'zayavka.q.experience': '¿Has participado en licitaciones?',
  'zayavka.q.experience.won_this_year': 'He ganado este año',
  'zayavka.q.experience.won_last_year': 'Gané el año pasado',
  'zayavka.q.experience.participated': 'He participado, sin ganar',
  'zayavka.q.experience.never': 'No he participado',
  'zayavka.q.esign': '¿Tienes certificado digital?',
  'zayavka.q.esign.yes': 'Sí',
  'zayavka.q.esign.no': 'No',
  'zayavka.q.form': 'Forma jurídica',
  'zayavka.q.form.legal': 'Sociedad (S.L., S.A.…)',
  'zayavka.q.form.ip': 'Autónomo',
  'zayavka.q.form.self_employed': 'Profesional independiente',
  'zayavka.q.form.individual': 'Particular',
  'zayavka.q.budget': '¿Qué presupuesto de licitación te encaja?',
  'zayavka.q.budget.lt_100m': 'Hasta 10.000 €',
  'zayavka.q.budget.100m_1b': '10.000 € – 100.000 €',
  'zayavka.q.budget.1b_10b': '100.000 € – 1 M€',
  'zayavka.q.budget.gt_10b': 'Más de 1 M€',
  'zayavka.s3.title': 'Cómo contactarte',
  'zayavka.id.label': 'NIF o NIE',
  'zayavka.id.invalid': 'El NIF y el NIE tienen 9 caracteres',
  'zayavka.phone.label': 'Teléfono',
  'zayavka.phone.country': 'Prefijo',
  'zayavka.phone.invalid': 'Revisa el número',
  'zayavka.submit': 'Enviar',
  'zayavka.submitting': 'Enviando…',
  'zayavka.consent': 'Al enviar, aceptas que te contactemos por teléfono o por mensaje',
  'zayavka.error.network': 'Sin conexión: vuelve a pulsar',
  'zayavka.error.rateLimited': 'Demasiadas solicitudes desde esta red. Escríbenos a',
  'zayavka.error.generic': 'No se ha podido enviar. Escríbenos a',
  'zayavka.thanks.title': 'Gracias por tu solicitud',
  'zayavka.thanks.body':
    'Revisaremos tu perfil y te contactaremos, normalmente en un día laborable',
  'zayavka.thanks.register': 'Registrarse gratis',
  // Chile (cl) — rendered on the Liciora build only (prices IVA incluido 19 %).
  'plans.vat.includedCl': '· IVA incluido',
  'plans.vat.includedBreakdown': 'Incluye {{vat}} de IVA ({{rate}} %)',
  'plans.cardOnlyPriceVatIncluded': '{{amount}} / mes · IVA incluido',
  // Chile (cl) — what the buyer receives instead of an SII factura (a US seller's Stripe
  // receipt).
  'plans.receiptCl':
    'Recibes por email un comprobante de pago; si agregas tu RUT y razón social al pagar, aparecen en él. No emitimos factura electrónica del SII.',
  'billing.stripe.receiptCl':
    'Te llega por email un comprobante de pago. No emitimos factura electrónica del SII.',
} as const;
